import React, { useState, useEffect, useCallback, useRef } from 'react';
import './styles.css';
import {
  setSbUrl as _setSbUrl, setSbKey as _setSbKey, setSbToken as _setSbToken,
  sbSignUp, sbSignIn, sbSignOut, sbRefreshSession,
  sbSignInWithGoogle, sbResetPassword,
  sbGetProfile, sbSaveProfile, sbCheckUsername, sbDeleteAccount,
  sbGet, sbUpsert, sbDelete, sbUploadPhoto,
} from './lib/supabase.js';
import { loadLocal, saveLocal } from './lib/storage.js';
import {
  DAYS, MONTHS, FOOD_ILLUSTRATIONS, DIETARY_OPTIONS, ALLERGEN_OPTIONS,
  getWeekDates, fmtDate, dispDate, catColor, fmtQty,
  normaliseIngredient, downloadIcs, shareRecipePdf,
} from './lib/utils.js';

// Local mutable Supabase state — never imported from the module (imports are read-only in ES modules)
let _SB_URL = import.meta.env.VITE_SUPABASE_URL || '';
let _SB_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
let _SB_TOKEN = '';
function syncSb(url, key, token) {
  if (url !== undefined) { _SB_URL = url; _setSbUrl(url); }
  if (key !== undefined) { _SB_KEY = key; _setSbKey(key); }
  if (token !== undefined) { _SB_TOKEN = token; _setSbToken(token); }
}


// printRecipePdf — opens a printable recipe card window (same HTML as shareRecipePdf)
function printRecipePdf(recipe, servings) {
  shareRecipePdf(recipe, servings);
}

// ─── Toast hook ───────────────────────────────────────────────────────────────
function useToasts(){
  const [toasts,setToasts]=useState([]);
  const add=useCallback((msg,type="info")=>{
    const id=Date.now();
    setToasts(p=>[...p,{id,msg,type}]);
    setTimeout(()=>setToasts(p=>p.filter(t=>t.id!==id)),3500);
  },[]);
  return{toasts,add};
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN APP
// ─────────────────────────────────────────────────────────────────────────────
function App(){
  // ── Auth state ────────────────────────────────────────────────────────────
  const [session,setSession]=useState(()=>{
    // Check for OAuth callback in URL hash (Google sign in returns token in hash)
    try{
      const hash = window.location.hash;
      if(hash && hash.includes("access_token")){
        const params = new URLSearchParams(hash.replace("#",""));
        const accessToken = params.get("access_token");
        const refreshToken = params.get("refresh_token");
        const expiresIn = parseInt(params.get("expires_in")||"3600");
        if(accessToken){
          // Clear hash from URL
          window.history.replaceState(null,"",window.location.pathname);
          // Build session from OAuth tokens — fetch user info
          const sess = {
            access_token: accessToken,
            refresh_token: refreshToken,
            expires_at: Math.floor(Date.now()/1000) + expiresIn,
            user: { email: "" }, // will be populated below
          };
          syncSb(undefined,undefined,accessToken);
          // Parse uid directly from JWT — available immediately, no async needed
          let uid = null;
          try{ uid = JSON.parse(atob(accessToken.split(".")[1])).sub; }catch(e){}
          if(uid) sess.user = {...sess.user, id: uid};
          // Fetch full user details and update session + localStorage
          fetch(`${_SB_URL}/auth/v1/user`, {
            headers: { apikey: _SB_KEY, Authorization: `Bearer ${accessToken}` }
          }).then(r=>r.json()).then(u=>{
            const fullSess = {...sess, user: {...u, id: u.id||uid}};
            localStorage.setItem("pw_session", JSON.stringify(fullSess));
            // Trigger a session update so the app re-renders with full user object
            window.__platewiseSessionUpdate?.(fullSess);
          }).catch(()=>{});
          localStorage.setItem("pw_session", JSON.stringify(sess));
          return sess;
        }
      }
      const s = localStorage.getItem("pw_session");
      return s ? JSON.parse(s) : null;
    }catch{return null;}
  });
  // Allow OAuth init code to update session once user details are fetched
  React.useEffect(()=>{
    window.__platewiseSessionUpdate=(fullSess)=>{
      setSession(fullSess);
      syncSb(undefined,undefined,fullSess.access_token);
    };
    return()=>{ window.__platewiseSessionUpdate=null; };
  },[]);

  const [authMode,setAuthMode]=useState("signin"); // "signin" | "signup" | "reset" | "profile-setup"
  const [authEmail,setAuthEmail]=useState("");
  const [authPassword,setAuthPassword]=useState("");
  const [authConfirm,setAuthConfirm]=useState("");
  const [authLoading,setAuthLoading]=useState(false);
  const [authError,setAuthError]=useState("");
  const [authMsg,setAuthMsg]=useState("");

  // User profile
  const [profile,setProfile]=useState(null); // {username, first_name, avatar_type, avatar_illustration, avatar_url, dietary, allergens, created_at}
  const [showProfileModal,setShowProfileModal]=useState(false);
  const [needsProfileSetup,setNeedsProfileSetup]=useState(false);
  // Belt-and-braces: if user has completed setup before, don't show it again
  // even if profile fetch fails temporarily
  const profileSetupDoneKey="pw_profile_setup_done";

  const [tab,setTab]=useState("recipes");
  const [recipes,setRecipes]=useState(()=>loadLocal("pw_recipes",[]));
  const [selected,setSelected]=useState([]);
  const [mealPlan,setMealPlan]=useState(()=>loadLocal("pw_mealplan",{}));
  const [weekOffset,setWeekOffset]=useState(0);
  const [calModal,setCalModal]=useState(false);
  const [detailRecipe,setDetailRecipe]=useState(null);
  const [editRecipe,setEditRecipe]=useState(null);
  const [filter,setFilter]=useState("all");
  const [search,setSearch]=useState("");
  const [addMealModal,setAddMealModal]=useState(null);
  const [modalSearch,setModalSearch]=useState("");
  const [recs,setRecs]=useState([]);
  const [recLoading,setRecLoading]=useState(false);
  const [checked,setChecked]=useState(()=>loadLocal("pw_checked",{}));
  const [cupboard,setCupboard]=useState(()=>loadLocal("pw_cupboard",{})); // { "garlic": true, ... }
  const [cupboardSearch,setCupboardSearch]=useState("");
  const [ingredientDB,setIngredientDB]=useState([]); // [{id,name,aliases,category,shelf_days,default_unit}]
  const [isAdmin,setIsAdmin]=useState(false);
  const [shareToAll,setShareToAll]=useState(false); // toggle when saving recipe
  const [importUrl,setImportUrl]=useState("");
  const [pendingRecipe,setPendingRecipe]=useState(null); // recipe waiting for review before saving

  const [importing,setImporting]=useState(false);
  const [pasteText,setPasteText]=useState("");
  const [importMode,setImportMode]=useState("url"); // "url" | "paste"
  const [importSteps,setImportSteps]=useState([]);
  const [debugInfo,setDebugInfo]=useState("");
  const [servingsOvr,setServingsOvr]=useState({});
  const [sbUrl,setSbUrl]=useState(()=>{ const v=loadLocal("pw_sburl",import.meta.env.VITE_SUPABASE_URL||""); syncSb(v,undefined,undefined); return v; });
  const [sbKey,setSbKey]=useState(()=>{ const v=loadLocal("pw_sbkey",import.meta.env.VITE_SUPABASE_ANON_KEY||""); syncSb(undefined,v,undefined); return v; });
  const [dbConn,setDbConn]=useState(false);

  // Set token from session whenever session changes
  useEffect(()=>{
    if(session?.access_token){
      syncSb(undefined,undefined,session.access_token);
      // Use env vars as fallback if state hasn't loaded from localStorage yet
      const url=sbUrl||import.meta.env.VITE_SUPABASE_URL||_SB_URL;
      const key=sbKey||import.meta.env.VITE_SUPABASE_ANON_KEY||_SB_KEY;
      if(url&&key){
        syncSb(url,key,undefined);
        setDbConn(true);
        // Load user data if we have a real session (not guest)
        if(!session.guest){
          loadUserData(session.access_token);
        }
      }
    }
  },[session?.access_token]);

  // Check admin status whenever session or connection changes
  useEffect(()=>{
    if(session?.guest){setIsAdmin(false);return;}
    const token = session?.access_token||_SB_TOKEN;
    const url = sbUrl||_SB_URL;
    const key = sbKey||_SB_KEY;
    if(!token||!url||!key)return;

    // Parse user ID from JWT token (works for both email and Google OAuth)
    let uid = session?.user?.id;
    if(!uid && token){
      try{
        const payload = JSON.parse(atob(token.split('.')[1]));
        uid = payload.sub;
      }catch(e){}
    }
    if(!uid)return;

    const timer = setTimeout(()=>{
      fetch(`${url}/rest/v1/admins?user_id=eq.${uid}&select=user_id`,{
        headers:{apikey:key, Authorization:`Bearer ${token}`}
      })
      .then(r=>{
        if(!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(data=>{
        const HARDCODED_ADMINS=(import.meta.env.VITE_ADMIN_UID||"").split(",").filter(Boolean);
        const adminResult=(Array.isArray(data)&&data.length>0)||HARDCODED_ADMINS.includes(uid);
        setIsAdmin(adminResult);
      })
      .catch(e=>console.warn("Admin check failed:",e));
    }, 800);
    return ()=>clearTimeout(timer);
  },[session?.access_token, dbConn]);

  // Auto-refresh session 5 mins before expiry
  useEffect(()=>{
    if(!session?.refresh_token||!sbUrl||!sbKey)return;
    const expiresAt=(session.expires_at||0)*1000;
    const msUntilRefresh=expiresAt-Date.now()-(5*60*1000);
    if(msUntilRefresh<=0){
      sbRefreshSession(session.refresh_token).then(data=>{
        const newSess={...data,expires_at:Math.floor(Date.now()/1000)+data.expires_in};
        setSession(newSess);
        syncSb(undefined,undefined,data.access_token);
        localStorage.setItem("pw_session",JSON.stringify(newSess));
      }).catch(()=>{setSession(null);localStorage.removeItem("pw_session");});
      return;
    }
    const timer=setTimeout(()=>{
      sbRefreshSession(session.refresh_token).then(data=>{
        const newSess={...data,expires_at:Math.floor(Date.now()/1000)+data.expires_in};
        setSession(newSess);
        syncSb(undefined,undefined,data.access_token);
        localStorage.setItem("pw_session",JSON.stringify(newSess));
      }).catch(()=>{setSession(null);localStorage.removeItem("pw_session");});
    },Math.max(0,msUntilRefresh));
    return()=>clearTimeout(timer);
  },[session,sbUrl,sbKey]);
  const [uploadingPhoto,setUploadingPhoto]=useState(false);
  const photoRef=useRef(null);
  const {toasts,add:toast}=useToasts();

  const weekDates=getWeekDates(weekOffset);
  const todayStr=fmtDate(new Date());

  const getCurrentUserId=()=>{
    if(session?.guest)return null;
    if(session?.user?.id)return session.user.id;
    const token=session?.access_token||_SB_TOKEN;
    if(token){
      try{return JSON.parse(atob(token.split(".")[1])).sub;}catch(e){}
    }
    return null;
  };


  const displaySubmittedBy=(recipe)=>{
    const username=recipe?.profiles?.username||(recipe?.user_id===getCurrentUserId()?profile?.username:null);
    return username?``:(recipe?.user_id?"Platewise user":"Platewise");
  };

  // persist
  useEffect(()=>saveLocal("pw_recipes",recipes),[recipes]);
  useEffect(()=>saveLocal("pw_mealplan",mealPlan),[mealPlan]);
  useEffect(()=>saveLocal("pw_checked",checked),[checked]);
  useEffect(()=>saveLocal("pw_cupboard",cupboard),[cupboard]);

  // Sync cupboard changes to Supabase
  const prevCupboardRef = React.useRef(cupboard);
  useEffect(()=>{
    const url=sbUrl||_SB_URL;
    const key=sbKey||_SB_KEY;
    const token=session?.access_token||_SB_TOKEN;
    if(!url||!key||!token||session?.guest)return;
    syncSb(url,key,token);
    const prev=prevCupboardRef.current;
    const curr=cupboard;
    // Find added items
    Object.keys(curr).forEach(async name=>{
      if(curr[name]&&!prev[name]){
        try{await sbUpsert("cupboard",{ingredient:name});}
        catch(e){console.warn("Cupboard add failed:",e);}
      }
    });
    // Find removed items
    Object.keys(prev).forEach(async name=>{
      if(prev[name]&&!curr[name]){
        try{await sbDelete("cupboard",{ingredient:name});}
        catch(e){console.warn("Cupboard remove failed:",e);}
      }
    });
    prevCupboardRef.current=curr;
  },[cupboard]);
  useEffect(()=>{ saveLocal("pw_sburl",sbUrl); syncSb(sbUrl,undefined,undefined); },[sbUrl]);
  useEffect(()=>{ saveLocal("pw_sbkey",sbKey); syncSb(undefined,sbKey,undefined); },[sbKey]);

  // ── Supabase ────────────────────────────────────────────────────────────────
  // ── Auth handlers ──────────────────────────────────────────────────────────
  const handleSignIn=async()=>{
    if(!authEmail||!authPassword){setAuthError("Please enter email and password");return;}
    setAuthLoading(true);setAuthError("");
    try{
      syncSb(sbUrl,sbKey,undefined);
      const data=await sbSignIn(authEmail,authPassword);
      const sess={...data,expires_at:Math.floor(Date.now()/1000)+data.expires_in};
      syncSb(undefined,undefined,data.access_token);
      setSession(sess);
      setDbConn(true);
      localStorage.setItem("pw_session",JSON.stringify(sess));
      toast(`👋 Welcome back!`,"success");
    }catch(e){setAuthError(e.message);}
    setAuthLoading(false);
  };

  const handleSignUp=async()=>{
    if(!authEmail||!authPassword){setAuthError("Please enter email and password");return;}
    if(authPassword!==authConfirm){setAuthError("Passwords don't match");return;}
    if(authPassword.length<6){setAuthError("Password must be at least 6 characters");return;}
    setAuthLoading(true);setAuthError("");
    try{
      syncSb(sbUrl,sbKey,undefined);
      await sbSignUp(authEmail,authPassword);
      setAuthMsg("✅ Account created! Check your email to confirm, then sign in.");
      setAuthMode("signin");
    }catch(e){setAuthError(e.message);}
    setAuthLoading(false);
  };

  const handleDeleteAccount=async()=>{
    const url=sbUrl||_SB_URL;const key=sbKey||_SB_KEY;const token=session?.access_token||_SB_TOKEN;
    const uid=session?.user?.id||(_SB_TOKEN?JSON.parse(atob(_SB_TOKEN.split('.')[1])).sub:null);
    if(!uid||!url||!key||!token)return;
    try{
      await sbDeleteAccount(uid, token, key);
      setSession(null);syncSb(undefined,undefined,"");setDbConn(false);setIsAdmin(false);setProfile(null);
      localStorage.removeItem("pw_session");
      setRecipes([]);setMealPlan({});setSelected([]);setChecked({});setCupboard({});
      toast("Account deleted","info");
    }catch(e){ toast("Delete failed: "+e.message,"error"); }
  };

  const handleSignOut=async()=>{
    if(session?.access_token) await sbSignOut(session.access_token).catch(()=>{});
    setSession(null);syncSb(undefined,undefined,"");setDbConn(false);setIsAdmin(false);
    localStorage.removeItem("pw_session");
    setRecipes([]);setMealPlan({});setSelected([]);setChecked({});setCupboard({});
    toast("Signed out","info");
  };

  const handlePasswordReset=async()=>{
    if(!authEmail){setAuthError("Enter your email address");return;}
    setAuthLoading(true);setAuthError("");
    try{
      syncSb(sbUrl,sbKey,undefined);
      await sbResetPassword(authEmail);
      setAuthMsg("✅ Password reset email sent! Check your inbox.");
      setAuthMode("signin");
    }catch(e){setAuthError(e.message);}
    setAuthLoading(false);
  };

  const fetchCommunityRecipes=async()=>{
    setCommunityLoading(true);
    try{
      const url=_SB_URL;
      const key=_SB_KEY;
      if(!url||!key){setCommunityLoading(false);return;}
      const token=_SB_TOKEN||key;
      let res=await fetch(`${url}/rest/v1/recipes?or=(is_public.eq.true,is_shared.eq.true)&select=*`,{
        headers:{apikey:key,Authorization:`Bearer ${token}`}
      });
      // Some Supabase projects do not have the profiles relationship exposed.
      // Fall back to recipes only so public sharing still works.
      if(!res.ok){
        res=await fetch(`${url}/rest/v1/recipes?or=(is_public.eq.true,is_shared.eq.true)&select=*`,{
          headers:{apikey:key,Authorization:`Bearer ${token}`}
        });
      }
      if(res.ok){
        const data=await res.json();
        setCommunityRecipes(Array.isArray(data)?data:[]);
      } else {
        console.warn("Community fetch failed:", await res.text());
      }
    }catch(e){console.warn("Community fetch failed:",e);}
    setCommunityLoading(false);
  };

  const saveCommunityRecipe=async(recipe)=>{
    // Save a copy of a community recipe to the user's own library
    const {profiles, ...recipeData}=recipe;
    const copy={
      ...recipeData,
      id:Date.now(),
      user_id:undefined, // trigger will set this
      is_public:false,
      is_shared:false,
      title:recipe.title, // keep original title
    };
    setRecipes(p=>[...p,copy]);
    await pushRecipe(copy,false);
    toast(`"${recipe.title}" added to your library!`,"success");
  };

  const loadUserData=async(token)=>{
    const url=sbUrl||import.meta.env.VITE_SUPABASE_URL||_SB_URL;
    const key=sbKey||import.meta.env.VITE_SUPABASE_ANON_KEY||_SB_KEY;
    if(!url||!key)return;
    try{
      const activeToken=token||_SB_TOKEN||session?.access_token;
      syncSb(url,key,activeToken);
      // Always try to get uid from the token passed in first (most reliable for Google OAuth)
      // Only use session.user.id if it's a real UUID (Google OAuth sets it to "" initially)
      const rawUid=session?.user?.id;
      let uid=(rawUid&&rawUid.length>10)?rawUid:null;
      if(!uid&&activeToken){
        try{ uid=JSON.parse(atob(activeToken.split('.')[1])).sub; }catch(e){}
      }
      if(!uid&&_SB_TOKEN){
        try{ uid=JSON.parse(atob(_SB_TOKEN.split('.')[1])).sub; }catch(e){}
      }
      // loadUserData uid: (logging removed for security)
      // Only load recipes owned by this user into the personal Recipe Library.
      // Public/community recipes are loaded separately in fetchCommunityRecipes().
      const safeUid = uid ? encodeURIComponent(uid) : null;
      const personalRecipesPromise = safeUid
        ? fetch(`${url}/rest/v1/recipes?user_id=eq.${safeUid}&select=*`,{
            headers:{apikey:key,Authorization:`Bearer ${activeToken}`}
          }).then(r=>r.ok?r.json():[])
        : Promise.resolve([]);

      const [rdata,mdata,cdata,adata,pdata,ingdata]=await Promise.all([
        personalRecipesPromise,
        sbGet("meal_plan"),
        sbGet("cupboard"),
        sbGet("admins"),
        uid?sbGetProfile(uid,activeToken,sbKey||_SB_KEY):Promise.resolve(null),
        fetch(`${url}/rest/v1/ingredients?select=*&order=name`,{headers:{apikey:key,Authorization:`Bearer ${token}`}}).then(r=>r.ok?r.json():[]).catch(()=>[]),
      ]);
      // Check if current user is admin - parse UID from JWT as fallback
      let userId=session?.user?.id;
      if(!userId && _SB_TOKEN){
        try{ userId=JSON.parse(atob(_SB_TOKEN.split('.')[1])).sub; }catch(e){}
      }
      const HARDCODED_ADMINS=(import.meta.env.VITE_ADMIN_UID||"").split(",").filter(Boolean);
      const adminCheck=(Array.isArray(adata)&&adata.some(a=>a.user_id===userId))||HARDCODED_ADMINS.includes(userId);
      setIsAdmin(adminCheck);
      // Load profile
      if(Array.isArray(ingdata)&&ingdata.length>0)setIngredientDB(ingdata);
      if(pdata){
        setProfile(pdata);
        setNeedsProfileSetup(false);
        saveLocal(profileSetupDoneKey, true);
      } else if(uid) {
        // No profile found — this is a new user, show setup
        // Only prompt setup if they haven't completed it before
        const doneBefore=loadLocal(profileSetupDoneKey, false);
        if(!doneBefore) setNeedsProfileSetup(true);
        // No profile found — showing setup
      } else {
        console.warn("Could not determine uid — skipping profile setup");
      }
      // Load user recipes — new users start empty
      if(Array.isArray(rdata)&&rdata.length>0){
        setRecipes(rdata);
      } else {
        setRecipes([]);
        saveLocal("pw_recipes",[]);
      }
      // Meal plan
      if(Array.isArray(mdata)&&mdata.length>0){
        const plan={};
        mdata.forEach(r=>{if(!plan[r.date])plan[r.date]=[];plan[r.date].push(r.recipe_id);});
        setMealPlan(plan);
      } else {
        setMealPlan({});
      }
      // Cupboard
      if(Array.isArray(cdata)&&cdata.length>0){
        const cb={};
        cdata.forEach(r=>{cb[r.ingredient]=true;});
        setCupboard(cb);
        // Prompt to review cupboard if items exist
        const count=cdata.length;
        setTimeout(()=>toast(`🧺 You have ${count} item${count===1?"":"s"} in your cupboard — visit the Cupboard tab to make sure everything is still in stock!`,"info"),1500);
      } else {
        setCupboard({});
      }
    }catch(e){console.warn("Load user data failed:",e);}
  };

  const connectSupabase=async()=>{
    if(!sbUrl||!sbKey){toast("Enter both URL and key","error");return;}
    // Sanitise — strip trailing slash
    const url=sbUrl.trim().replace(/\/+$/,"");
    const key=sbKey.trim();
    syncSb(url,key,undefined);
    toast("Connecting…","info");
    try{
      const res=await fetch(`${url}/rest/v1/recipes?select=id&limit=1`,{
        method:"GET",
        mode:"cors",
        headers:{
          "apikey":key,
          "Authorization":`Bearer ${key}`,
          "Content-Type":"application/json",
        },
      });
      if(res.status===401){toast("❌ Invalid API key — check your anon key","error");return;}
      if(res.status===404){toast("❌ Table not found — did you run the setup SQL?","error");return;}
      if(!res.ok){toast(`❌ Error ${res.status} — check your Project URL`,"error");return;}
      setDbConn(true);
      toast("☁ Connected to Supabase!","success");
      // pull data
      let userId=session?.user?.id;
      if(!userId && _SB_TOKEN){
        try{ userId=JSON.parse(atob(_SB_TOKEN.split('.')[1])).sub; }catch(e){}
      }
      // Fetch all recipes owned by this user (user_id match)
      // Also fetch any public recipes they created that may have user_id=null (legacy)
      const personalUrl = userId
        ? `${url}/rest/v1/recipes?user_id=eq.${encodeURIComponent(userId)}&select=*`
        : null;
      const [rdata,mdata]=await Promise.all([
        personalUrl
          ? fetch(personalUrl,{mode:"cors",headers:{"apikey":key,"Authorization":`Bearer ${_SB_TOKEN||key}`}}).then(r=>r.ok?r.json():[])
          : Promise.resolve([]),
        fetch(`${url}/rest/v1/meal_plan?select=*`,{mode:"cors",headers:{"apikey":key,"Authorization":`Bearer ${_SB_TOKEN||key}`}}).then(r=>r.json()),
      ]);
      if(Array.isArray(rdata)&&rdata.length>0){
        setRecipes(rdata);
      } else {
        setRecipes([]);
      }
      if(Array.isArray(mdata)&&mdata.length>0){
        const plan={};
        mdata.forEach(r=>{if(!plan[r.date])plan[r.date]=[];plan[r.date].push(r.recipe_id);});
        setMealPlan(plan);
      }
    }catch(e){
      console.error("Supabase connect error:",e);
      toast("❌ Connection blocked — this preview iframe cannot reach external URLs. Use the Export Backup button and connect from your own hosted app.","error");
      setDbConn(false);
    }
  };

  const pushRecipe=async(recipe, makeShared=false)=>{
    // Always use local _SB_ vars — the imported lib values may be stale
    const url=sbUrl||_SB_URL;
    const key=sbKey||_SB_KEY;
    if(!url||!key){
      toast("⚠️ Not connected to database — recipe saved locally only","error");
      return;
    }

    // Refresh session token if expired before attempting save
    let token=session?.access_token||_SB_TOKEN;
    if(!token){
      toast("⚠️ Not signed in — recipe saved locally only","error");
      return;
    }
    try{
      const expiresAt=(session?.expires_at||0)*1000;
      const isExpired=expiresAt>0&&Date.now()>expiresAt-30000;
      if(isExpired&&session?.refresh_token){
        const refreshed=await sbRefreshSession(session.refresh_token);
        if(refreshed?.access_token){
          const newSess={...refreshed,expires_at:Math.floor(Date.now()/1000)+refreshed.expires_in};
          setSession(newSess);
          syncSb(undefined,undefined,refreshed.access_token);
          localStorage.setItem("pw_session",JSON.stringify(newSess));
          token=refreshed.access_token;
        }
      }
    }catch(e){
      console.warn("Token refresh failed:",e);
      // Continue with existing token — may still work
    }
    let userId=session?.user?.id;
    if(!userId && token){
      try{ userId=JSON.parse(atob(token.split('.')[1])).sub; }catch(e){}
    }
    if(!userId && !makeShared){
      toast("⚠️ Could not identify user — recipe saved locally only","error");
      return;
    }
    try{
      const {profiles, ...cleanRecipe}=recipe;
      const canPublish=!session?.guest&&!!userId;
      const recipeToSave={
        ...cleanRecipe,
        user_id: makeShared ? null : userId,
        is_shared: !!makeShared,
        is_public: makeShared ? false : (canPublish ? !!cleanRecipe.is_public : false),
      };
      // Use direct fetch with local vars — never use sbUpsert here as it reads stale module state
      // Strip local JS id — Supabase auto-generates int8 id
      // Also strip UI-only fields that don't exist as DB columns
      const {id:localId, methodStr, tagsStr, ...recipeWithoutId}=recipeToSave;
      // Only send id if it looks like a real Supabase int (not a Date.now() timestamp)
      const isRealDbId=typeof localId==="number"&&localId<2147483647;
      const payload=isRealDbId?{...recipeWithoutId,id:localId}:recipeWithoutId;

      const res=await fetch(`${url}/rest/v1/recipes`,{
        method:"POST",
        headers:{
          apikey:key,
          Authorization:`Bearer ${token}`,
          "Content-Type":"application/json",
          Prefer:"resolution=merge-duplicates,return=representation",
        },
        body:JSON.stringify(payload),
      });
      if(!res.ok){
        const err=await res.text();
        console.error("pushRecipe failed:",err);
        toast(`❌ Recipe not saved to database: ${err.slice(0,80)}`,"error");
        return;
      }
      // Update local recipe with the real Supabase-generated id
      const saved=await res.json().catch(()=>null);
      const realId=Array.isArray(saved)?saved[0]?.id:saved?.id;
      if(realId&&realId!==localId){
        setRecipes(p=>p.map(r=>r.id===localId?{...r,id:realId}:r));
      }
      // Recipe saved successfully
    }catch(e){
      console.error("pushRecipe error:",e);
      toast("❌ Database error: "+e.message,"error");
    }
  };

  // ── Photo upload ────────────────────────────────────────────────────────────
  const handlePhoto=async(file,recipeId)=>{
    if(!file?.type.startsWith("image/")){toast("Please select an image file","error");return;}
    if(file.size>5*1024*1024){toast("Image must be under 5MB","error");return;}
    setUploadingPhoto(true);

    const updateImage=(url)=>{
      setRecipes(p=>p.map(r=>{
        if(r.id!==recipeId)return r;
        const updated={...r,image:url};
        // Persist the new image URL to Supabase
        pushRecipe(updated);
        return updated;
      }));
      if(detailRecipe?.id===recipeId)setDetailRecipe(p=>({...p,image:url}));
    };

    if(dbConn){
      try{
        const ext=file.name.split(".").pop();
        const path=`${recipeId}_${Date.now()}.${ext}`;
        const url=await sbUploadPhoto(path,file);
        updateImage(url);
        toast("📷 Photo uploaded!","success");
      }catch{
        // fallback to base64
        toBase64(file,url=>{updateImage(url);toast("📷 Photo saved","info");});
      }
    }else{
      toBase64(file,url=>{updateImage(url);toast("📷 Photo saved locally","success");});
    }
    setUploadingPhoto(false);
  };

  function toBase64(file,cb){
    const r=new FileReader();
    r.onload=e=>cb(e.target.result);
    r.readAsDataURL(file);
  }

  // ── Import via Claude + web search ─────────────────────────────────────────
  const updateStep=(label,status)=>setImportSteps(p=>{
    const i=p.findIndex(s=>s.label===label);
    if(i>=0)return p.map((s,j)=>j===i?{...s,status}:s);
    return[...p,{label,status}];
  });

  const handleImport=async()=>{
    if(!importUrl.trim()||importing)return;
    setImporting(true);
    setImportSteps([]);
    setDebugInfo("");
    updateStep("Fetching page","active");
    updateStep("Extracting recipe","pending");
    updateStep("Structuring data","pending");
    updateStep("Saving to library","pending");

    try{
      let recipe = null;
      const activeUrl=(sbUrl||_SB_URL||"").trim().replace(/\/+$/,"");
      const activeKey=(sbKey||_SB_KEY||"").trim();

      // ── Route ALL URLs through Supabase Edge Function ──────────────────────
      // It handles Gousto via direct API, and all other sites via JSON-LD scraping
      if(activeUrl && activeKey){
        try{
          // Use JWT session token as Bearer, anon key as apikey
          const activeToken = session?.access_token || _SB_TOKEN || activeKey;
          const fnRes = await fetch(`${activeUrl}/functions/v1/smart-worker`,{
            method:"POST",
            headers:{
              "Content-Type":"application/json",
              "Authorization":`Bearer ${activeToken}`,
              "apikey":activeKey,
            },
            body:JSON.stringify({url:importUrl}),
          });
          const fnText = await fnRes.text();
          let fnData = {};
          try{ fnData = JSON.parse(fnText); }catch(e){ fnData={parseError:fnText.slice(0,500)}; }

          updateStep("Fetching page","done");
          console.debug("Import debug:", JSON.stringify(fnData,null,2).slice(0,500));

          if(fnData.recipe){
            recipe={
              id:Date.now(),
              title:fnData.recipe.title||"Imported Recipe",
              image:fnData.recipe.image||"https://images.unsplash.com/photo-1495521821757-a1efb6729352?w=400&q=80",
              time:fnData.recipe.time||30,
              servings:fnData.recipe.servings||2,
              cuisine:fnData.recipe.cuisine||"",
              tags:fnData.recipe.tags||[],
              ingredients:(fnData.recipe.ingredients||[]).map(i=>({
                name:String(i.name||""),qty:(()=>{ const q=i.qty; if(!q&&q!==0)return 1; const n=Number(q); if(!isNaN(n)&&n>0)return n; const s=String(q).trim(); const frac=s.match(/^(\d+)\s*\/\s*(\d+)$/); if(frac)return Math.round(parseInt(frac[1])/parseInt(frac[2])*100)/100; const range=s.match(/^([\d.]+)\s*[-–]\s*[\d.]+$/); if(range)return Number(range[1])||1; return 1; })(),unit:String(i.unit||""),
                shelfDays:Number(i.shelfDays)||7,category:(i.category&&i.category.trim())||"pantry",
              })),
              method:fnData.recipe.method||[],
            };
            updateStep("Extracting recipe","done");
          } else {
            updateStep("Extracting recipe","error");
          }
        }catch(edgeErr){
          updateStep("Fetching page","done");
          console.debug("Edge function error:", edgeErr);
        }
      } else {
        updateStep("Fetching page","done");
        
      }

      // ── Fallback: Claude web search (for sites that block server fetching) ──
      if(!recipe){
        updateStep("Extracting recipe","active");
        const prompt=`Extract the recipe from this URL and return ONLY valid JSON, no markdown:
{
  "title":"string","time":30,"servings":4,"cuisine":"string","tags":["tag"],
  "ingredients":[{"name":"string","qty":1,"unit":"string","shelfDays":7,"category":"produce|meat|fish|dairy|pantry"}],
  "method":["Step 1"],"image":""
}
URL: ${importUrl}`;

        const res=await fetch("https://api.anthropic.com/v1/messages",{
          method:"POST",
          headers:{"Content-Type":"application/json"},
          body:JSON.stringify({
            model:"claude-sonnet-4-20250514",
            max_tokens:2000,
            tools:[{type:"web_search_20250305",name:"web_search"}],
            messages:[{role:"user",content:prompt}],
          }),
        });
        const data=await res.json();
        updateStep("Extracting recipe","done");
        updateStep("Structuring data","active");
        const text=(data.content||[]).map(b=>b.text||"").join("");
        const match=text.match(/\{[\s\S]*\}/);
        if(!match)throw new Error("No JSON found");
        const parsed=JSON.parse(match[0]);
        if(!parsed.title||!Array.isArray(parsed.ingredients))throw new Error("Invalid structure");
        recipe={
          id:Date.now(),is_imported:true,...parsed,
          image:parsed.image||"https://images.unsplash.com/photo-1495521821757-a1efb6729352?w=400&q=80",
          ingredients:(parsed.ingredients||[]).map(i=>({
            name:String(i.name||""),qty:(()=>{ const q=i.qty; if(!q&&q!==0)return 1; const n=Number(q); if(!isNaN(n)&&n>0)return n; const s=String(q).trim(); const frac=s.match(/^(\d+)\s*\/\s*(\d+)$/); if(frac)return Math.round(parseInt(frac[1])/parseInt(frac[2])*100)/100; const range=s.match(/^([\d.]+)\s*[-–]\s*[\d.]+$/); if(range)return Number(range[1])||1; return 1; })(),unit:String(i.unit||""),
            shelfDays:Number(i.shelfDays)||7,category:(i.category&&i.category.trim())||"pantry",
          })),
          method:Array.isArray(parsed.method)?parsed.method:[],
          tags:Array.isArray(parsed.tags)?parsed.tags:[],
        };
      }

      if(!recipe)throw new Error("Could not extract recipe");

      updateStep("Structuring data","done");
      // Apply ingredient normalisation before showing review
      recipe.ingredients = recipe.ingredients.map(normaliseIngredient);
      updateStep("Saving to library","pending");
      // Show review modal instead of saving immediately
      setPendingRecipe(recipe);
      setImportSteps([]);
      setDebugInfo("");
      toast("✅ Recipe extracted! Review and confirm below.","success");

    }catch(err){
      setImportSteps(p=>p.map(s=>s.status==="active"||s.status==="pending"?{...s,status:"error"}:s));
      toast("❌ Import failed — please try again","error");
      setTimeout(()=>setImportSteps([]),5000);
    }
    setImporting(false);
  };

    const handlePasteImport=async()=>{
    if(!pasteText.trim()||importing)return;
    setImporting(true);
    setImportSteps([]);
    updateStep("Reading recipe text","active");
    updateStep("Structuring data","pending");
    updateStep("Saving to library","pending");

    const prompt=`You are a recipe extraction expert. Extract the recipe from the text below and return ONLY a valid JSON object — no markdown, no backticks, no explanation.

JSON structure:
{
  "title": "string",
  "time": <total minutes as number>,
  "servings": <number>,
  "cuisine": "string",
  "tags": ["tag1","tag2"],
  "ingredients": [
    {"name":"string","qty":<number>,"unit":"string or empty","shelfDays":<number>,"category":"produce|meat|fish|dairy|pantry"}
  ],
  "method": ["Step 1","Step 2"],
  "image": ""
}

Shelf life guide: fresh herbs 3-5d, fish 1-2d, raw meat/chicken 2-3d, dairy 7-30d, fresh veg 3-14d, dried pasta/rice 365d, spices 365d, canned goods 730d.

RECIPE TEXT:
${pasteText.slice(0,6000)}

Return ONLY the JSON object.`;

    try{
      const res=await fetch("https://api.anthropic.com/v1/messages",{
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body:JSON.stringify({
          model:"claude-sonnet-4-20250514",
          max_tokens:2000,
          messages:[{role:"user",content:prompt}],
        }),
      });
      const data=await res.json();
      updateStep("Reading recipe text","done");
      updateStep("Structuring data","active");

      const text=(data.content||[]).map(b=>b.text||"").join("");
      const match=text.match(/\{[\s\S]*\}/);
      if(!match)throw new Error("No JSON found");
      const parsed=JSON.parse(match[0]);
      if(!parsed.title||!Array.isArray(parsed.ingredients))throw new Error("Invalid structure");

      const recipe={
        id:Date.now(),
        ...parsed,
        image:parsed.image||"https://images.unsplash.com/photo-1495521821757-a1efb6729352?w=400&q=80",
        ingredients:(parsed.ingredients||[]).map(i=>({
          name:String(i.name||""),qty:(()=>{ const q=i.qty; if(!q&&q!==0)return 1; const n=Number(q); if(!isNaN(n)&&n>0)return n; const s=String(q).trim(); const frac=s.match(/^(\d+)\s*\/\s*(\d+)$/); if(frac)return Math.round(parseInt(frac[1])/parseInt(frac[2])*100)/100; const range=s.match(/^([\d.]+)\s*[-–]\s*[\d.]+$/); if(range)return Number(range[1])||1; return 1; })(),unit:String(i.unit||""),
          shelfDays:Number(i.shelfDays)||7,category:(i.category&&i.category.trim())||"pantry",
        })),
        method:Array.isArray(parsed.method)?parsed.method.filter(Boolean):[String(parsed.method||"")],
        tags:Array.isArray(parsed.tags)?parsed.tags:[],
      };

      updateStep("Structuring data","done");
      updateStep("Saving to library","active");
      setRecipes(p=>[...p,recipe]);
      await pushRecipe(recipe);
      updateStep("Saving to library","done");
      setPasteText("");
      setImportMode("url");
      toast(`✅ "${recipe.title}" added to your library!`,"success");
      setTimeout(()=>setImportSteps([]),3000);
    }catch(err){
      console.error("Paste import error:",err);
      setImportSteps(p=>p.map(s=>s.status==="active"||s.status==="pending"?{...s,status:"error"}:s));
      toast("❌ Couldn't extract recipe — make sure you pasted the full recipe text","error");
      setTimeout(()=>setImportSteps([]),4000);
    }
    setImporting(false);
  };

  // ── AI Recommendations ──────────────────────────────────────────────────────
  const getRecommendations=useCallback(async(selIds)=>{
    if(!selIds.length){setRecs([]);return;}
    setRecLoading(true);
    const selR=recipes.filter(r=>selIds.includes(r.id));
    const unsel=recipes.filter(r=>!selIds.includes(r.id));
    if(!unsel.length){setRecLoading(false);return;}
    const prompt=`Meal planning assistant. User selected:
${selR.map(r=>`- ${r.title}: ${r.ingredients.map(i=>i.name).join(", ")}`).join("\n")}

Available (use only IDs from this list):
${unsel.map(r=>`ID:${r.id} "${r.title}": ${r.ingredients.map(i=>i.name).join(", ")}`).join("\n")}

Pick 2-3 recipes that share the most ingredients (to reduce waste) OR complement the meal variety best. Return ONLY JSON: [{"id":2,"reason":"12 words max"}]`;
    try{
      const res=await fetch("https://api.anthropic.com/v1/messages",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({model:"claude-sonnet-4-20250514",max_tokens:300,messages:[{role:"user",content:prompt}]})});
      const data=await res.json();
      const text=data.content?.[0]?.text||"[]";
      const m=text.match(/\[[\s\S]*\]/);
      const parsed=m?JSON.parse(m[0]):[];
      setRecs(parsed.filter(r=>unsel.some(u=>u.id===r.id)));
    }catch{
      const allIngs=selR.flatMap(r=>r.ingredients.map(i=>i.name.toLowerCase()));
      setRecs(unsel.map(r=>({id:r.id,score:r.ingredients.filter(i=>allIngs.includes(i.name.toLowerCase())).length,reason:"Shares ingredients with your selections"})).filter(r=>r.score>0).sort((a,b)=>b.score-a.score).slice(0,3));
    }
    setRecLoading(false);
  },[recipes]);

  useEffect(()=>{getRecommendations(selected);},[selected]);

  // ── Shopping list ───────────────────────────────────────────────────────────
  const shopList=(function computeShopList(mp,rcp,ovr){
    const items={};
    Object.entries(mp).forEach(([ds,ids])=>{
      const md=new Date(ds+"T00:00:00");
      ids.forEach(rid=>{
        const r=rcp.find(x=>x.id===rid);if(!r)return;
        const scale=(ovr[rid]||r.servings)/r.servings;
        r.ingredients.forEach(ing=>{
          const key=ing.name.toLowerCase();
          const sq=ing.qty*scale;
          if(!items[key]){
            items[key]={...ing,qty:sq,mealDate:md,latestMealDate:md};
          } else {
            items[key].qty+=sq;
            // Track earliest date (for use-by warning) and latest date (to show as use-by prompt)
            if(md<items[key].mealDate)items[key].mealDate=md;
            if(md>items[key].latestMealDate)items[key].latestMealDate=md;
          }
        });
      });
    });
    const t=new Date();t.setHours(0,0,0,0);
    return Object.values(items).map(item=>{
      const days=Math.max(0,Math.ceil((item.mealDate-t)/86400000));
      const status=item.shelfDays>=days?"ok":days<=2?"urgent":"warn";
      // Format use-by date for meat and produce
      const latestDate=item.latestMealDate;
      const useByDate=(item.category==="meat"||item.category==="produce"||item.category==="fish")
        ?latestDate.toLocaleDateString("en-GB",{weekday:"short",day:"numeric",month:"short"})
        :null;
      return{...item,daysUntilMeal:days,dateStatus:status,useByDate};
    }).filter(i=>i&&i.name).sort((a,b)=>(a.category||"pantry").localeCompare(b.category||"pantry"));
  })(mealPlan,recipes,servingsOvr);

  // ── Helpers ─────────────────────────────────────────────────────────────────
  // All known ingredient names — DB first (canonical), then local recipe ingredients
  const allIngredients = [...new Set([
    ...ingredientDB.map(i=>i.name),
    ...recipes.flatMap(r => r.ingredients.map(i => i.name)).filter(Boolean),
  ])].sort();

  const toggleSel=id=>setSelected(p=>p.includes(id)?p.filter(x=>x!==id):[...p,id]);

  // ── Ingredient DB helpers ───────────────────────────────────────────────────

  // Fetch all ingredients from Supabase ingredients table
  const fetchIngredientDB=async()=>{
    const url=sbUrl||_SB_URL; const key=sbKey||_SB_KEY;
    if(!url||!key)return;
    try{
      const res=await fetch(`${url}/rest/v1/ingredients?select=*&order=name`,{
        headers:{apikey:key,Authorization:`Bearer ${_SB_TOKEN||key}`}
      });
      if(res.ok){
        const data=await res.json();
        if(Array.isArray(data))setIngredientDB(data);
      }
    }catch(e){console.warn("fetchIngredientDB failed:",e);}
  };

  // Look up an ingredient by name or alias — returns the DB row or null
  const lookupIngredient=(name)=>{
    if(!name||!ingredientDB.length)return null;
    const n=name.toLowerCase().trim();
    return ingredientDB.find(ing=>{
      if(ing.name.toLowerCase()===n)return true;
      if(ing.aliases?.some(a=>a.toLowerCase()===n))return true;
      return false;
    })||null;
  };

  // Upsert an ingredient into the DB — uses guessShelfLife as fallback
  const upsertIngredient=async(ingName,category,shelfDays)=>{
    const url=sbUrl||_SB_URL; const key=sbKey||_SB_KEY; const token=_SB_TOKEN;
    if(!url||!key||!token||!ingName?.trim())return;
    const canonical=ingName.trim();
    // Don't re-insert if already exists
    if(lookupIngredient(canonical))return;
    try{
      const newIng={
        name:canonical,
        category:category||"pantry",
        shelf_days:shelfDays||guessShelfLife(canonical,category),
        default_unit:"",
        aliases:[],
      };
      const res=await fetch(`${url}/rest/v1/ingredients`,{
        method:"POST",
        headers:{apikey:key,Authorization:`Bearer ${token}`,"Content-Type":"application/json",Prefer:"resolution=ignore,return=representation"},
        body:JSON.stringify(newIng),
      });
      if(res.ok){
        const saved=await res.json();
        const row=Array.isArray(saved)?saved[0]:saved;
        if(row)setIngredientDB(p=>[...p.filter(i=>i.name!==canonical),row]);
      }
    }catch(e){console.warn("upsertIngredient failed:",e);}
  };

  // ── Shelf-life optimiser ─────────────────────────────────────────────────────
  // Returns a new mealPlan redistributing meals across the same days in freshness order.
  // Most perishable (shortest min shelfDays) recipes go on the earliest available days.
  const optimisePlanByShelfLife = (plan, recipeList, currentWeekDates) => {
    // Collect all date keys that have meals, sorted chronologically
    const allDates = Object.keys(plan)
      .filter(d => plan[d]?.length > 0)
      .sort();
    if (allDates.length === 0) return plan;

    // Flatten all meal IDs across the plan (preserving multiple meals per day)
    const allMealIds = allDates.flatMap(d => plan[d]);

    // Score each recipe by its most perishable ingredient (lowest shelfDays)
    const scoreRecipe = (rid) => {
      const r = recipeList.find(x => x.id === rid);
      if (!r || !r.ingredients?.length) return 999;
      return Math.min(...r.ingredients.map(i => Number(i.shelfDays) || 999));
    };

    // Sort meal IDs: most perishable (lowest score) first
    const sorted = [...allMealIds].sort((a, b) => scoreRecipe(a) - scoreRecipe(b));

    // Rebuild plan: keep the same days but redistribute sorted meals
    // Maintain the same number of meals per day as before
    const newPlan = { ...plan };
    let idx = 0;
    for (const d of allDates) {
      const count = plan[d].length;
      newPlan[d] = sorted.slice(idx, idx + count);
      idx += count;
    }
    return newPlan;
  };

  const addToDay=async(date,rid)=>{
    setMealPlan(p=>{const e=p[date]||[];return e.includes(rid)?p:{...p,[date]:[...e,rid]};});
    setAddMealModal(null);
    toast("Meal added to planner","success");
    // Save to Supabase with user_id
    const url=sbUrl||_SB_URL;
    const key=sbKey||_SB_KEY;
    const token=session?.access_token||_SB_TOKEN;
    if(url&&key&&token){
      try{
        syncSb(url,key,token);
        const userId=session?.user?.id;
        await sbUpsert("meal_plan",{date,recipe_id:rid,user_id:userId||undefined});
      }catch(e){console.warn("Meal plan save failed:",e);}
    }
  };
  const removeFromDay=async(date,rid)=>{
    setMealPlan(p=>({...p,[date]:(p[date]||[]).filter(id=>id!==rid)}));
    const url=sbUrl||_SB_URL;const key=sbKey||_SB_KEY;const token=session?.access_token||_SB_TOKEN;
    if(url&&key&&token&&!session?.guest){
      try{
        syncSb(url,key,token);
        // Delete matching row from meal_plan
        const res=await fetch(`${_SB_URL}/rest/v1/meal_plan?date=eq.${date}&recipe_id=eq.${rid}&limit=1`,{
          method:"DELETE",
          headers:{apikey:_SB_KEY,Authorization:`Bearer ${_SB_TOKEN}`,Prefer:"return=minimal"},
        });
        if(!res.ok)console.warn("Meal plan delete failed:",await res.text());
      }catch(e){console.warn("Meal plan delete failed:",e);}
    }
  };
  const deleteRecipe=async(id)=>{
    if(!confirm("Delete this recipe?"))return;
    setRecipes(p=>p.filter(r=>r.id!==id));
    setSelected(p=>p.filter(x=>x!==id));
    setMealPlan(p=>{const n={};Object.entries(p).forEach(([d,ids])=>{n[d]=ids.filter(x=>x!==id);});return n;});
    setDetailRecipe(null);
    toast("Recipe deleted","info");
    const url=sbUrl||_SB_URL;const key=sbKey||_SB_KEY;const token=session?.access_token||_SB_TOKEN;
    if(url&&key&&token&&!session?.guest){
      try{
        syncSb(url,key,token);
        // Delete recipe by ID only (works for both own and shared/admin recipes)
        await fetch(`${_SB_URL}/rest/v1/recipes?id=eq.${id}`,{
          method:"DELETE",
          headers:{apikey:_SB_KEY,Authorization:`Bearer ${_SB_TOKEN}`,Prefer:"return=minimal"},
        });
        // Clean up any meal plan entries referencing this recipe
        await fetch(`${_SB_URL}/rest/v1/meal_plan?recipe_id=eq.${id}`,{
          method:"DELETE",
          headers:{apikey:_SB_KEY,Authorization:`Bearer ${_SB_TOKEN}`,Prefer:"return=minimal"},
        });
      }catch(e){console.warn("Recipe delete from Supabase failed:",e);}
    }
  };
  const saveRecipe=(r, makeShared=false)=>{
    const uid=getCurrentUserId();
    const existing=recipes.find(x=>x.id===r.id);
    const originalAuthor=existing?.user_id||r.user_id||uid;
    const canChangePublic=!session?.guest&&!!uid&&(!originalAuthor||originalAuthor===uid);
    if(!canChangePublic) r={...r,is_public:!!existing?.is_public};
    // Normalise ingredient names
    r = {...r, user_id: makeShared ? null : (originalAuthor||r.user_id), ingredients: r.ingredients.map(normaliseIngredient)};
    // Enrich from ingredient DB and upsert any new ingredients in background
    r = {...r, ingredients: r.ingredients.map(ing=>{
      const dbRow=lookupIngredient(ing.name);
      if(dbRow){
        return{...ing,
          category:ing.category||dbRow.category||"pantry",
          shelfDays:ing.shelfDays||dbRow.shelf_days||7,
        };
      }
      // New ingredient — add to DB in background
      upsertIngredient(ing.name,ing.category,ing.shelfDays);
      return ing;
    })};
    // Always keep in local library — public recipes belong to the user too
    const exists = recipes.some(x=>x.id===r.id);
    if(exists){
      setRecipes(p=>p.map(x=>x.id===r.id?r:x));
    } else {
      setRecipes(p=>[...p,r]);
    }
    if(detailRecipe?.id===r.id)setDetailRecipe(r);
    pushRecipe(r, makeShared);
    setEditRecipe(null);
    setAddMode(null);
    // If public, also refresh community recipes so it shows up immediately
    if(r.is_public) setTimeout(()=>fetchCommunityRecipes(), 1000);
    toast(`✅ Recipe ${makeShared?"added to shared library":r.is_public?"saved and shared with the community":"saved"}!`,"success");
  };

  // Detect dietary requirement clash for a recipe

  // Smart shelf life lookup — returns estimated days based on ingredient name/category
  const guessShelfLife=(name,category)=>{
    const n=(name||"").toLowerCase();
    // Fresh herbs
    if(n.includes("fresh ")||n.includes(" herb")||["basil","coriander","parsley","chives","dill","tarragon","chervil","mint"].some(h=>n.includes(h)))return 5;
    // Dried herbs and spices
    if(n.includes("dried ")||n.includes("powder")||n.includes("ground ")||n.includes("spice")||["oregano","thyme","rosemary","cumin","paprika","turmeric","cinnamon","nutmeg","cayenne","chilli flakes","bay leaves"].some(s=>n.includes(s)))return 365;
    // Long shelf life condiments/sauces
    if(["soy sauce","fish sauce","worcestershire","oyster sauce","hot sauce","tabasco","vinegar","olive oil","vegetable oil","sesame oil"].some(s=>n.includes(s)))return 365;
    // Stocks and broths
    if(n.includes("stock")||n.includes("broth")||n.includes("bouillon"))return n.includes("fresh")?5:365;
    // Canned/tinned goods
    if(n.includes("tinned")||n.includes("canned")||n.includes("tin of")||n.includes("can of"))return 730;
    // Pasta, rice, grains
    if(["pasta","spaghetti","penne","rice","couscous","quinoa","lentil","chickpea","flour","oats"].some(p=>n.includes(p)))return 365;
    // Dairy
    if(["parmesan","pecorino","hard cheese"].some(c=>n.includes(c)))return 30;
    if(["milk","cream","yoghurt","yogurt","creme fraiche","sour cream","cottage cheese"].some(c=>n.includes(c)))return 7;
    if(["butter","ghee"].some(c=>n.includes(c)))return 21;
    if(["mozzarella","feta","brie","camembert","ricotta"].some(c=>n.includes(c)))return 7;
    if(n.includes("cheese"))return 14;
    // Eggs
    if(n==="egg"||n==="eggs"||n.startsWith("egg ")||n.startsWith("eggs "))return 21;
    // Fish
    if(["salmon","tuna","cod","haddock","mackerel","sardine","prawn","shrimp","crab","lobster","mussel","squid","pollock","plaice","halibut","seabass","sea bass","trout","herring","anchovy","tilapia","monkfish"].some(f=>n.includes(f)))return 2;
    // Meat
    if(["mince","ground beef","ground lamb"].some(m=>n.includes(m)))return 2;
    if(["chicken breast","chicken thigh","chicken fillet","turkey"].some(m=>n.includes(m)))return 3;
    if(["lamb","beef","pork","bacon","steak","sausage"].some(m=>n.includes(m)))return 3;
    if(n.includes("pancetta")||n.includes("chorizo")||n.includes("salami")||n.includes("ham"))return 7;
    // Produce
    if(["lettuce","spinach","rocket","watercress","herbs"].some(p=>n.includes(p)))return 5;
    if(["tomato","cucumber","courgette","aubergine","pepper","mushroom","avocado"].some(p=>n.includes(p)))return 5;
    if(["carrot","parsnip","potato","sweet potato","butternut","onion","garlic","ginger"].some(p=>n.includes(p)))return 14;
    if(["lemon","lime","orange","apple","pear"].some(p=>n.includes(p)))return 14;
    if(n.includes("banana"))return 5;
    // Category fallbacks
    if(category==="fish")return 2;
    if(category==="meat")return 3;
    if(category==="dairy")return 7;
    if(category==="produce")return 7;
    return 14; // pantry default
  };

  const getDietaryClash=(recipe)=>{
    if(!profile?.dietary?.length||!recipe?.ingredients?.length)return null;
    const ings=recipe.ingredients.map(i=>(i.name||"").toLowerCase());
    const has=(words)=>ings.some(i=>words.some(w=>i.includes(w)));
    const hasMeat=has(["chicken","beef","pork","lamb","bacon","ham","sausage","mince","steak","turkey","duck","veal","venison","pancetta","chorizo","salami","pepperoni","lard","gelatin"]);
    const hasFish=has(["salmon","tuna","cod","haddock","mackerel","sardine","prawn","shrimp","crab","lobster","mussel","squid","anchovy","fish"]);
    const hasDairy=has(["milk","cream","butter","cheese","yoghurt","yogurt","parmesan","mozzarella","cheddar","feta","brie","ricotta","mascarpone","creme fraiche","ghee","whey"]);
    const hasEgg=ings.some(i=>i==="egg"||i==="eggs"||i.startsWith("egg ")||i.startsWith("eggs "));
    const hasGluten=has(["flour","breadcrumb","pasta","wheat","barley","rye","couscous","semolina"]);
    const hasNuts=ings.some(i=>["almond","cashew","pistachio","walnut","pecan","hazelnut","peanut"].some(n=>i===n||i.startsWith(n+" ")||i.endsWith(" "+n)||i.includes(" "+n+" ")));
    const hasCarbs=has(["sugar","flour","bread","pasta","rice","potato","oats","honey","syrup","corn starch"]);
    const d=profile.dietary;
    if(d.includes("Vegan")){
      if(hasMeat)return"Contains meat";
      if(hasFish)return"Contains fish";
      if(hasDairy)return"Contains dairy";
      if(hasEgg)return"Contains eggs";
    }
    if(d.includes("Vegetarian")&&(hasMeat||hasFish)){
      return hasMeat?"Contains meat":"Contains fish";
    }
    if(d.includes("Dairy-free")&&hasDairy)return"Contains dairy";
    if(d.includes("Gluten-free")&&hasGluten)return"Contains gluten";
    if(d.includes("Nut-free")&&hasNuts)return"Contains nuts";
    if(d.includes("Keto")&&hasCarbs)return"High in carbs";
    return null;
  };

  const newRecipe=()=>setEditRecipe({id:Date.now(),title:"",image:"",time:30,servings:4,cuisine:"",tags:[],ingredients:[],method:[]});
  const navTo=t=>{setTab(t);setDetailRecipe(null);setEditRecipe(null);};

  // Ingredients already in shopping list
  const shoppingIngNames = new Set(shopList.map(i=>i.name.toLowerCase()));

  // Ingredients from SELECTED recipes (for overlap badge - shown when recipes are selected)
  const selectedIngNames = new Set(
    recipes.filter(r=>selected.includes(r.id))
      .flatMap(r=>r.ingredients.map(i=>i.name.toLowerCase()))
  );
  // Cupboard ingredient names
  const cupboardIngNames = new Set(Object.keys(cupboard).filter(k=>cupboard[k]));

  // Combine: cupboard always counts; add selected ings or shopping list when available
  const overlapNames = new Set([
    ...(selected.length > 0 ? selectedIngNames : shopList.length > 0 ? shoppingIngNames : []),
    ...cupboardIngNames,
  ]);

  const [sortBy,setSortBy]=useState("match");
  const [filterOpen,setFilterOpen]=useState(false);
  const [addMode,setAddMode]=useState(null); // null | "choice" | "url" | "manual" | "community" | "photo"
  const [communityRecipes,setCommunityRecipes]=useState([]);
  const [communityLoading,setCommunityLoading]=useState(false);
  const [communitySearch,setCommunitySearch]=useState("");
  const [communityFilter,setCommunityFilter]=useState("all");
  const [communitySortBy,setCommunitySortBy]=useState("match");
  const filterRef = useRef(null);
  useEffect(()=>{
    function handleClick(e){
      if(filterRef.current&&!filterRef.current.contains(e.target)) setFilterOpen(false);
    }
    document.addEventListener("mousedown",handleClick);
    return()=>document.removeEventListener("mousedown",handleClick);
  },[]); // "match"|"time"|"alpha"

  // IDs of AI-recommended recipes to avoid duplication in main grid
  const aiRecipeIds=new Set((recs||[]).map(rec=>rec.id));

  const currentUserId=session?.user?.id||(_SB_TOKEN?(()=>{try{return JSON.parse(atob(_SB_TOKEN.split('.')[1])).sub;}catch(e){return null;}})():null);
  const libraryRecipes=recipes.filter(r=>!r.is_public || (currentUserId && r.user_id===currentUserId));
  const cuisines=["all",...new Set(libraryRecipes.map(r=>r.cuisine))];

  const filtRec=libraryRecipes.filter(r=>{
    const flt=filter==="all"||r.cuisine===filter;
    const srch=r.title.toLowerCase().includes(search.toLowerCase())||r.tags?.some(t=>t.includes(search.toLowerCase()));
    // Hide AI suggested recipes from main grid to avoid duplication
    const notAiDup=!aiRecipeIds.has(r.id)||selected.includes(r.id);
    return flt&&srch&&notAiDup;
  }).sort((a,b)=>{
    if(sortBy==="alpha") return a.title.localeCompare(b.title);
    if(sortBy==="time") return (a.time||0)-(b.time||0);
    // Default: sort by ingredient match score
    const scoreA=a.ingredients.filter(i=>overlapNames.has(i.name.toLowerCase())).length;
    const scoreB=b.ingredients.filter(i=>overlapNames.has(i.name.toLowerCase())).length;
    return scoreB-scoreA;
  });
  const recIds=recs.map(r=>r.id);

  const detSvg=detailRecipe?(servingsOvr[detailRecipe.id]||detailRecipe.servings):1;
  const detScale=detailRecipe?detSvg/detailRecipe.servings:1;

  const shopCheckedCount=shopList.filter(i=>{
    const k=i.name.toLowerCase().trim();
    return !!checked[k]||Object.keys(cupboard).some(ck=>ck.toLowerCase().trim()===k&&cupboard[ck]);
  }).length;

  // Show profile setup for new users (after sign in but before main app)
  if(session && !session.guest && needsProfileSetup){
    return(
      <ProfileSetupScreen
        session={session}
        loading={authLoading}
        setLoading={setAuthLoading}
        onSave={(p)=>{
          setProfile(p);
          setNeedsProfileSetup(false);
          saveLocal(profileSetupDoneKey, true);
          toast("🎉 Welcome to Platewise!","success");
        }}
      />
    );
  }

  if(!session){
    return(
      <AuthScreen
        mode={authMode} setMode={setAuthMode}
        email={authEmail} setEmail={setAuthEmail}
        password={authPassword} setPassword={setAuthPassword}
        confirm={authConfirm} setConfirm={setAuthConfirm}
        loading={authLoading} error={authError} msg={authMsg}
        onSignIn={handleSignIn} onSignUp={handleSignUp} onReset={handlePasswordReset}
        onClearMsg={()=>setAuthMsg("")} onClearErr={()=>setAuthError("")}
        sbUrl={sbUrl} setSbUrl={v=>{setSbUrl(v);syncSb(v,undefined,undefined);saveLocal("pw_sburl",v);}}
        sbKey={sbKey} setSbKey={v=>{setSbKey(v);syncSb(undefined,v,undefined);saveLocal("pw_sbkey",v);}}
        onSkip={async()=>{
          setSession({guest:true,user:{email:"Guest"}});
          setIsAdmin(false);
          // Load shared recipes for guests if Supabase is configured
          const url=sbUrl||_SB_URL;
          const key=sbKey||_SB_KEY;
          if(url&&key){
            try{
              const res=await fetch(`${url}/rest/v1/recipes?is_shared=eq.true&select=*`,{
                headers:{apikey:key,Authorization:`Bearer ${key}`}
              });
              if(res.ok){
                const shared=await res.json();
                if(Array.isArray(shared)&&shared.length>0){
                  setRecipes(Array.isArray(shared)?shared:[]);
                }
              }
            }catch(e){console.warn("Failed to load shared recipes for guest:",e);}
          }
        }}
      />
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  return(
    <>
    <div className="app">
      {/* NAV */}
      <nav className="nav">
        <div className="brand">plate<span>wise</span></div>
        <span className={`sync-pill ${dbConn?"cloud":"local"}`}>{dbConn?"☁ Synced":"💾 Local"}</span>
        {isAdmin&&!session?.guest&&<span style={{fontSize:11,padding:"3px 10px",borderRadius:20,background:"rgba(99,102,241,0.3)",color:"#a5b4fc",fontWeight:500}}>⭐ Admin</span>}
        <div className="nav-tabs">
          {[["recipes","📖 Recipes"],["planner","📅 Planner"],["shopping","🛒 Shopping"],["cupboard","🧺 Cupboard"],["settings","⚙ Settings"]].map(([id,label])=>(
            <button key={id} className={`ntab ${tab===id?"active":""}`} onClick={()=>navTo(id)}>{label}</button>
          ))}
        </div>
        <button
          onClick={()=>setShowProfileModal(true)}
          style={{width:36,height:36,borderRadius:"50%",background:"rgba(255,255,255,0.12)",border:"1.5px solid rgba(255,255,255,0.2)",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,marginLeft:8,overflow:"hidden"}}
          title={profile?`@${profile.username}`:"Profile & Settings"}
        >
          {(()=>{
            if((profile?.avatar_type==="food-photo"||profile?.avatar_type==="upload")&&profile?.avatar_url)
              return <img src={profile.avatar_url} style={{width:"100%",height:"100%",objectFit:"cover"}}/>;
            const illus=FOOD_ILLUSTRATIONS.find(([k])=>k===(profile?.avatar_illustration||"person1"));
            const av=illus?.[1];
            return <div style={{width:"100%",height:"100%",background:av?.bg||"#f5f0e8",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18}}>{av?.emoji||"👤"}</div>;
          })()}
        </button>
      </nav>

      <main className="main">

        {/* ══════════ RECIPES ══════════ */}
        {tab==="recipes"&&!detailRecipe&&!editRecipe&&!addMode&&(<>
          <div className="sh" style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:12}}>
            <div>
              <h2>Recipe Library</h2>
              <p>Select meals to plan your week — AI suggests complementary recipes to reduce shopping waste</p>
            </div>
            <button
              onClick={()=>setAddMode("choice")}
              style={{width:38,height:38,borderRadius:"50%",background:"var(--terra)",color:"white",border:"none",fontSize:22,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",boxShadow:"0 2px 8px rgba(196,103,74,0.35)",flexShrink:0,marginTop:4}}
              title="Add recipe"
            >+</button>
          </div>



          {/* Selection bar */}
          {selected.length>0&&(
            <div className="selbar">
              <p><strong>{selected.length}</strong> selected</p>
              <div className="schips">{selected.map(id=>{const r=recipes.find(x=>x.id===id);return<div key={id} className="schip" onClick={()=>toggleSel(id)} style={{cursor:"pointer"}} title="Click to unselect">{r?.title}<button onClick={e=>{e.stopPropagation();toggleSel(id);}}>×</button></div>;})}</div>
              <button className="btn btn-p" style={{flex:"none"}} onClick={()=>navTo("planner")}>Plan Week →</button>
            </div>
          )}

          {/* Recommendations */}
          {selected.length>0&&(
            <div className="rec-sec">
              <div className="rec-head"><div style={{fontSize:24}}>✨</div><div><h3>AI Suggestions</h3><p>Share ingredients with your selection — buy less, waste less</p></div></div>
              {recLoading?(<div className="pulse"><div className="pd"/><div className="pd"/><div className="pd"/> Finding best matches…</div>):recs.length>0&&(
                <div className="grid">
                  {recs.map(rec=>{
                    const r=recipes.find(x=>x.id===rec.id);
                    if(!r||selected.includes(r.id))return null;
                    return(
                      <div key={r.id} className="card rec">
                        <span className="badge badge-rec">✨ Suggested</span>
                        <img className="card-img" src={r.image} alt={r.title} loading="lazy" onError={e=>e.target.src="https://images.unsplash.com/photo-1495521821757-a1efb6729352?w=400&q=80"}/>
                        <div className="card-body">
                          <div className="card-title">{r.title}</div>
                          <div className="card-meta"><span>⏱ {r.time}m</span><span>👤 {r.servings}</span><span>{r.cuisine}</span>
                            {(overlapNames.size>0)&&(()=>{
                              const shared=r.ingredients.filter(i=>overlapNames.has(i.name.toLowerCase())).length;
                              const total=r.ingredients.length;
                              const pct=total>0?shared/total:0;
                              const bg=pct>=0.5?"var(--sage)":pct>0?"var(--gold)":"#e2e8f0";
                              const col=pct>=0.5?"white":pct>0?"white":"#64748b";
                              return(<span style={{background:bg,color:col,fontSize:11,padding:"2px 7px",borderRadius:20,fontWeight:600}}>{shared}/{total} shared</span>);
                            })()}
                          </div>
                          <p style={{fontSize:12,color:"var(--soft)",marginBottom:10}}>{rec.reason}</p>
                          <div className="card-actions">
                            <button className="btn btn-sage" onClick={()=>toggleSel(r.id)}>+ Select</button>
                            <button className="btn btn-s btn-sm" onClick={()=>setDetailRecipe(r)}>View</button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Search + Filter + Sort */}
          <div style={{display:"flex",gap:8,marginBottom:10,alignItems:"center"}}>
            <input className="sinput" style={{flex:1}} value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search recipes…"/>
            <div ref={filterRef} style={{position:"relative",flexShrink:0}}>
              <button
                onClick={e=>{e.stopPropagation();setFilterOpen(p=>!p);}}
                style={{display:"flex",alignItems:"center",gap:6,padding:"8px 14px",borderRadius:"var(--rs)",border:`1.5px solid ${filter!=="all"||sortBy!=="match"?"var(--terra)":"var(--border)"}`,background:filter!=="all"||sortBy!=="match"?"var(--terra)":"white",color:filter!=="all"||sortBy!=="match"?"white":"var(--bark-mid)",fontSize:13,fontWeight:500,cursor:"pointer",fontFamily:"'DM Sans',sans-serif",whiteSpace:"nowrap"}}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="4" y1="6" x2="20" y2="6"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="11" y1="18" x2="13" y2="18"/></svg>
                {filter==="all"?"Filter":filter.charAt(0).toUpperCase()+filter.slice(1)}
              </button>
              {filterOpen&&(
                <div onClick={e=>e.stopPropagation()} style={{position:"absolute",top:"calc(100% + 6px)",right:0,zIndex:200,background:"white",border:"1.5px solid var(--border)",borderRadius:"var(--r)",boxShadow:"0 8px 24px rgba(0,0,0,0.12)",minWidth:200,overflow:"hidden"}}>
                  <div style={{padding:"8px 6px"}}>
                    <div style={{fontSize:10,fontWeight:600,color:"var(--soft)",textTransform:"uppercase",letterSpacing:"0.5px",padding:"4px 10px",marginBottom:4}}>Cuisine</div>
                    {cuisines.map(c=>(
                      <button key={c} onClick={()=>{setFilter(c);setFilterOpen(false);}}
                        style={{display:"flex",alignItems:"center",justifyContent:"space-between",width:"100%",padding:"8px 12px",background:filter===c?"var(--warm)":"transparent",border:"none",borderRadius:8,cursor:"pointer",fontSize:13,color:"var(--bark)",fontFamily:"'DM Sans',sans-serif",textAlign:"left"}}
                      >
                        {c==="all"?"All cuisines":c.charAt(0).toUpperCase()+c.slice(1)}
                        {filter===c&&<span style={{color:"var(--terra)",fontWeight:700}}>✓</span>}
                      </button>
                    ))}
                    <div style={{borderTop:"1px solid var(--border)",margin:"8px 0 4px"}}/>
                    <div style={{fontSize:10,fontWeight:600,color:"var(--soft)",textTransform:"uppercase",letterSpacing:"0.5px",padding:"4px 10px",marginBottom:4}}>Sort by</div>
                    {[["match","Ingredients matched"],["time","Cook time"],["alpha","A to Z"]].map(([val,label])=>(
                      <button key={val} onClick={()=>{setSortBy(val);setFilterOpen(false);}}
                        style={{display:"flex",alignItems:"center",justifyContent:"space-between",width:"100%",padding:"8px 12px",background:sortBy===val?"var(--warm)":"transparent",border:"none",borderRadius:8,cursor:"pointer",fontSize:13,color:"var(--bark)",fontFamily:"'DM Sans',sans-serif",textAlign:"left"}}
                      >
                        {label}
                        {sortBy===val&&<span style={{color:"var(--terra)",fontWeight:700}}>✓</span>}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Empty state — no recipes yet */}
          {recipes.length===0&&!addMode&&(
            <div className="empty" style={{textAlign:"center",padding:"60px 20px"}}>
              <div style={{fontSize:56,marginBottom:16}}>🍽</div>
              <h3 style={{fontFamily:"'Playfair Display',serif",fontSize:24,marginBottom:8,color:"var(--bark)"}}>Your recipe library is empty</h3>
              <p style={{fontSize:15,color:"var(--soft)",marginBottom:24,maxWidth:340,margin:"0 auto 24px"}}>Add your first recipe by importing from a URL, scanning a cookbook, or adding it manually.</p>
              <button className="btn btn-p" style={{fontSize:15,padding:"12px 28px"}} onClick={()=>setAddMode("choice")}>+ Add Your First Recipe</button>
            </div>
          )}

          {/* Grid */}
          <div className="grid">
            {filtRec.map(r=>{
              const isSel=selected.includes(r.id);
              const dietaryClash=!isSel?getDietaryClash(r):null;
              return(
                <div key={r.id} className={`card ${isSel?"sel":""} ${recIds.includes(r.id)&&!isSel?"rec":""}`}
                  style={dietaryClash?{border:"1.5px solid #fca5a5",background:"#fff8f8"}:{}}
                >
                  {isSel&&<span className="badge badge-sel">✓ Selected</span>}
                  {!isSel&&<span className="badge" style={{background:r.is_public?"#16a34a":"#64748b",color:"white",left:10,right:"auto"}}>{r.is_public?"🌍 Public":"🔒 Private"}</span>}
                  {profile?.allergens?.length>0&&r.ingredients?.some(i=>
                    profile.allergens.some(a=>i.name.toLowerCase().includes(a.toLowerCase()))
                  )&&(
                    <span className="badge" style={{background:"#dc2626",color:"white",right:10,left:"auto"}} title="Contains one of your allergens">!</span>
                  )}
                  <img className="card-img" src={r.image} alt={r.title} loading="lazy" onError={e=>e.target.src="https://images.unsplash.com/photo-1495521821757-a1efb6729352?w=400&q=80"}/>
                  <div className="card-body">
                    <div className="card-title">{r.title}</div>
                    <div style={{fontSize:11,color:"var(--soft)",margin:"2px 0 6px"}}>✍️ {displaySubmittedBy(r)}</div>
                    <div className="card-meta"><span>⏱ {r.time}m</span><span>👤 {r.servings}</span><span>{r.cuisine}</span>
                      {(overlapNames.size>0)&&!isSel&&(()=>{
                        const shared=r.ingredients.filter(i=>overlapNames.has(i.name.toLowerCase())).length;
                        const total=r.ingredients.length;
                        const pct=total>0?shared/total:0;
                        const bg=pct>=0.5?"var(--sage)":pct>0?"var(--gold)":"#e2e8f0";
                        const col=pct>=0.5?"white":pct>0?"white":"#64748b";
                        return(
                          <span style={{background:bg,color:col,fontSize:11,padding:"2px 7px",borderRadius:20,fontWeight:600,flexShrink:0}}>
                            {shared}/{total} shared
                          </span>
                        );
                      })()}
                    </div>
                    <div className="tags">{r.tags?.map(t=><span key={t} className="tag">{t}</span>)}</div>
                    <div className="card-actions">
                      <button className="btn btn-p" onClick={()=>toggleSel(r.id)} style={{background:isSel?"var(--sage)":undefined}}>{isSel?"✓ Selected":"+ Select"}</button>
                      <button className="btn btn-s btn-sm" onClick={()=>setDetailRecipe(r)}>View</button>
                    </div>
                    {dietaryClash&&!isSel&&(
                      <div style={{background:"#fef2f2",borderTop:"1px solid #fca5a5",margin:"8px -12px -12px",padding:"6px 12px",fontSize:11,color:"#b91c1c",fontWeight:500,display:"flex",alignItems:"center",gap:5,borderRadius:"0 0 var(--r) var(--r)"}}>
                        ⚠️ {dietaryClash}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

          </div>
        </>)}

        {/* ══════════ RECIPE DETAIL ══════════ */}
        {tab==="recipes"&&detailRecipe&&!editRecipe&&(
          <div className="detail">
            <div style={{display:"flex",gap:8,marginBottom:16,flexWrap:"wrap"}}>
              <button className="btn btn-g" onClick={()=>setDetailRecipe(null)}>← Back</button>
              {(()=>{
                const uid=getCurrentUserId();
                const canEdit=isAdmin||(detailRecipe.user_id&&detailRecipe.user_id===uid);
                return canEdit&&<button className="btn btn-s" onClick={()=>setEditRecipe({...detailRecipe})}>✏ Edit</button>;
              })()}
              {(()=>{
                const uid=getCurrentUserId();
                const canDelete=(detailRecipe.user_id&&detailRecipe.user_id===uid)||(!detailRecipe.user_id&&!detailRecipe.is_shared&&!detailRecipe.is_public)||(isAdmin&&detailRecipe.is_shared);
                return canDelete&&<button className="btn btn-del" onClick={()=>deleteRecipe(detailRecipe.id)}>🗑 Delete</button>;
              })()}

              <button className="btn btn-s" style={{flex:"none"}} onClick={()=>shareRecipePdf(detailRecipe,detSvg)} title="Share recipe as PDF">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
                Share PDF
              </button>
              <button className="btn btn-p" style={{flex:"none"}} onClick={()=>toggleSel(detailRecipe.id)}>{selected.includes(detailRecipe.id)?"✓ Selected":"+ Add to Plan"}</button>
            </div>

            <div style={{position:"relative",marginBottom:24}}>
              <img className="dimg" src={detailRecipe.image} alt={detailRecipe.title} onError={e=>e.target.src="https://images.unsplash.com/photo-1495521821757-a1efb6729352?w=400&q=80"}/>
              {((detailRecipe.user_id&&detailRecipe.user_id===getCurrentUserId())||(!detailRecipe.user_id&&!detailRecipe.is_shared&&!detailRecipe.is_public)||(isAdmin&&detailRecipe.is_shared))&&<button className="btn btn-s" style={{position:"absolute",bottom:12,right:12,background:"rgba(255,255,255,0.92)"}}
                onClick={()=>{if(photoRef.current){photoRef.current.dataset.rid=detailRecipe.id;photoRef.current.click();}}}>
                {uploadingPhoto?"Uploading…":"📷 Change Photo"}
              </button>}
            </div>

            <h2>{detailRecipe.title}</h2>
            <div className="dmeta">
              <div className="dmi"><span>⏱</span><span>{detailRecipe.time} min</span></div>
              <div className="dmi"><span>🍽</span><span>{detailRecipe.cuisine}</span></div>
              <div className="dmi"><span>👤</span><span>{detailRecipe.servings} base</span></div>
              <div className="dmi"><span>{detailRecipe.is_public?"🌍":"🔒"}</span><span>{detailRecipe.is_public?"Public":"Private"}</span></div>
              <div className="dmi"><span>✍️</span><span>{displaySubmittedBy(detailRecipe)}</span></div>
            </div>

            {/* Servings scaler */}
            <div className="sctl">
              <label>Adjust servings (affects shopping list quantities)</label>
              <button className="sbtn" disabled={detSvg<=1} onClick={()=>setServingsOvr(p=>({...p,[detailRecipe.id]:Math.max(1,detSvg-1)}))}>−</button>
              <strong>{detSvg}</strong>
              <button className="sbtn" onClick={()=>setServingsOvr(p=>({...p,[detailRecipe.id]:detSvg+1}))}>+</button>
              {detSvg!==detailRecipe.servings&&(<>
                <span className="scaled">{detSvg>detailRecipe.servings?"↑ scaled up":"↓ scaled down"}</span>
                <button className="btn btn-g btn-sm" onClick={()=>setServingsOvr(p=>{const n={...p};delete n[detailRecipe.id];return n;})}>Reset</button>
              </>)}
            </div>

            <div className="dsec">
              <h3>Ingredients <span style={{fontSize:13,color:"var(--soft)",fontFamily:"'DM Sans',sans-serif",fontWeight:400}}>for {detSvg} servings</span></h3>
              {detailRecipe.ingredients.map((ing,i)=>{
                const isShared=overlapNames.has(ing.name.toLowerCase())&&overlapNames.size>0;
                // Check if this specific ingredient clashes with dietary/allergen requirements
                const ingLower=ing.name.toLowerCase();
                const isAllergen=profile?.allergens?.length>0&&profile.allergens.some(a=>ingLower.includes(a.toLowerCase()));
                const MEAT_W=["chicken","beef","pork","lamb","bacon","ham","sausage","mince","steak","turkey","duck","veal","venison","pancetta","chorizo","salami","pepperoni","lard","gelatin"];
                const FISH_W=["salmon","tuna","cod","haddock","mackerel","sardine","prawn","shrimp","crab","lobster","mussel","squid","anchovy"];
                const DAIRY_W=["milk","cream","butter","cheese","yoghurt","yogurt","parmesan","mozzarella","cheddar","feta","brie","ricotta","mascarpone","ghee","whey"];
                const EGG_W=["egg","eggs"];
                const GLUTEN_W=["flour","breadcrumb","pasta","wheat","barley","rye","couscous","semolina"];
                const NUT_W=["almond","cashew","pistachio","walnut","pecan","hazelnut","peanut"];
                const d=profile?.dietary||[];
                const isMeat=MEAT_W.some(w=>ingLower.includes(w));
                const isFish=FISH_W.some(w=>ingLower.includes(w));
                const isDairy=DAIRY_W.some(w=>ingLower.includes(w));
                const isEgg=EGG_W.some(w=>ingLower===w||ingLower.startsWith(w+" "));
                const isGluten=GLUTEN_W.some(w=>ingLower.includes(w));
                const isNut=NUT_W.some(w=>ingLower===w||ingLower.startsWith(w+" ")||ingLower.endsWith(" "+w)||ingLower.includes(" "+w+" "));
                const isDietaryClash=(
                  ((d.includes("Vegan")||d.includes("Vegetarian"))&&(isMeat||isFish))||
                  (d.includes("Vegan")&&(isDairy||isEgg))||
                  (d.includes("Dairy-free")&&isDairy)||
                  (d.includes("Gluten-free")&&isGluten)||
                  (d.includes("Nut-free")&&isNut)
                );
                const isClash=isAllergen||isDietaryClash;
                return(
                  <div key={i} className="ingrow" style={{
                    background:isClash?"#fff0f0":isShared?"#f0faf0":"transparent",
                    borderRadius:6,paddingLeft:(isShared||isClash)?8:0,transition:"all 0.2s",
                    borderLeft:isClash?"3px solid #fca5a5":isShared?"3px solid var(--sage)":"none",
                  }}>
                    <div className="ingname" style={{color:isClash?"#b91c1c":isShared?"var(--sage)":"var(--text)",fontWeight:(isShared||isClash)?600:400}}>
                      {isClash&&<span style={{marginRight:5,fontSize:12}}>⚠</span>}
                      {isShared&&!isClash&&<span style={{marginRight:5,fontSize:12}}>✓</span>}
                      {ing.name}
                      {ing.notes&&<span style={{fontSize:12,fontWeight:400,color:"var(--soft)",marginLeft:5}}>({ing.notes})</span>}
                    </div>
                    <div className="ingr">
                      <span className="ingqty">{fmtQty(ing.qty*detScale)} {ing.unit}</span>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="dsec">
              <h3>Method</h3>
              {detailRecipe.method.map((step,i)=>(
                <div key={i} className="mstep">
                  <div className="snum">{i+1}</div>
                  <div className="stxt">{step}</div>
                </div>
              ))}
            </div>
          </div>
        )}


        {/* ══════════ ADD CHOICE SCREEN ══════════ */}
        {tab==="recipes"&&addMode==="choice"&&!editRecipe&&(
          <div style={{padding:"0 0 40px"}}>
            <div className="sh" style={{display:"flex",alignItems:"center",gap:12,marginBottom:8}}>
              <button onClick={()=>setAddMode(null)} style={{background:"none",border:"none",cursor:"pointer",color:"var(--soft)",fontSize:20,lineHeight:1,padding:0}}>←</button>
              <div><h2>Add a Recipe</h2><p>Choose how you'd like to add a recipe to your library</p></div>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:12}}>
              {/* Community */}
              <button
                onClick={()=>{setAddMode("community");fetchCommunityRecipes();}}
                style={{background:"white",border:"1.5px solid var(--border)",borderRadius:"var(--r)",padding:"20px 20px",textAlign:"left",cursor:"pointer",transition:"all 0.2s",display:"flex",alignItems:"center",gap:16}}
              >
                <div style={{width:48,height:48,borderRadius:14,background:"linear-gradient(135deg,#eef2ff,#c7d2fe)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:24,flexShrink:0}}>🌍</div>
                <div>
                  <div style={{fontFamily:"'Playfair Display',serif",fontSize:17,color:"var(--bark)",marginBottom:3}}>Browse Community</div>
                  <div style={{fontSize:13,color:"var(--soft)"}}>Discover recipes shared by other Platewise users and save them to your library</div>
                </div>
              </button>
              {/* URL Import */}
              <button
                onClick={()=>setAddMode("url")}
                style={{background:"white",border:"1.5px solid var(--border)",borderRadius:"var(--r)",padding:"20px 20px",textAlign:"left",cursor:"pointer",transition:"all 0.2s",display:"flex",alignItems:"center",gap:16}}
              >
                <div style={{width:48,height:48,borderRadius:14,background:"linear-gradient(135deg,#fff9ee,#fde68a)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:24,flexShrink:0}}>🔗</div>
                <div>
                  <div style={{fontFamily:"'Playfair Display',serif",fontSize:17,color:"var(--bark)",marginBottom:3}}>Import from URL</div>
                  <div style={{fontSize:13,color:"var(--soft)"}}>Paste a link from any recipe website — saved privately to your library only</div>
                </div>
              </button>
              {/* Photo Scan */}
              <button
                onClick={()=>setAddMode("photo")}
                style={{background:"white",border:"1.5px solid var(--border)",borderRadius:"var(--r)",padding:"20px 20px",textAlign:"left",cursor:"pointer",transition:"all 0.2s",display:"flex",alignItems:"center",gap:16}}
              >
                <div style={{width:48,height:48,borderRadius:14,background:"linear-gradient(135deg,#fdf4ff,#e9d5ff)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:24,flexShrink:0}}>📷</div>
                <div>
                  <div style={{fontFamily:"'Playfair Display',serif",fontSize:17,color:"var(--bark)",marginBottom:3}}>Scan a Recipe</div>
                  <div style={{fontSize:13,color:"var(--soft)"}}>Photograph a recipe book or handwritten card — AI extracts it automatically</div>
                </div>
              </button>
              {/* Manual */}
              <button
                onClick={()=>{setAddMode("manual");setEditRecipe({id:Date.now(),title:"",image:"",time:30,servings:4,cuisine:"",tags:[],ingredients:[],method:[],is_public:false});}}
                style={{background:"white",border:"1.5px solid var(--border)",borderRadius:"var(--r)",padding:"20px 20px",textAlign:"left",cursor:"pointer",transition:"all 0.2s",display:"flex",alignItems:"center",gap:16}}
              >
                <div style={{width:48,height:48,borderRadius:14,background:"linear-gradient(135deg,#f0faf0,#bbf7d0)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:24,flexShrink:0}}>✏️</div>
                <div>
                  <div style={{fontFamily:"'Playfair Display',serif",fontSize:17,color:"var(--bark)",marginBottom:3}}>Add Manually</div>
                  <div style={{fontSize:13,color:"var(--soft)"}}>Create a recipe from scratch — option to share with the community</div>
                </div>
              </button>
            </div>
          </div>
        )}

        {/* ══════════ URL IMPORT SCREEN ══════════ */}
        {tab==="recipes"&&addMode==="url"&&!editRecipe&&(
          <div>
            <div className="sh" style={{display:"flex",alignItems:"center",gap:12}}>
              <button onClick={()=>setAddMode("choice")} style={{background:"none",border:"none",cursor:"pointer",color:"var(--soft)",fontSize:20,lineHeight:1,padding:0}}>←</button>
              <div><h2>Import from URL</h2><p>Paste a link from any recipe website</p></div>
            </div>
            <div style={{background:"#fff9ee",border:"1px solid #f0d99a",borderRadius:"var(--r)",padding:"12px 16px",marginBottom:16,display:"flex",gap:10,alignItems:"flex-start"}}>
              <span style={{fontSize:18,flexShrink:0}}>⚠️</span>
              <p style={{fontSize:13,color:"#92400e",lineHeight:1.5}}>
                <strong>For personal use only.</strong> Recipes imported from URLs are saved privately to your library and cannot be shared to the community. Please respect the original creator's copyright.
              </p>
            </div>
            <div className="ibar" style={{marginBottom:12}}>
              <input className="iinput" value={importUrl} onChange={e=>setImportUrl(e.target.value)} placeholder="Paste a recipe URL to import…" onKeyDown={e=>e.key==="Enter"&&!importing&&handleImport()}/>
              <button className="btn btn-p" style={{flex:"none"}} onClick={handleImport} disabled={importing||!importUrl.trim()}>{importing?"Importing…":"Import"}</button>
            </div>
            {importSteps.length>0&&(
              <div className="psteps" style={{marginTop:6}}>
                {importSteps.map((s,i)=>(
                  <span key={i} className={`pstep ${s.status}`}>
                    {s.status==="done"?"✓":s.status==="active"?<span className="spin">⟳</span>:s.status==="error"?"✗":"○"} {s.label}
                  </span>
                ))}
              </div>
            )}
            {debugInfo&&(
              <div style={{marginTop:8}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
                  <span style={{fontSize:11,fontWeight:600,color:"var(--soft)",textTransform:"uppercase"}}>Debug</span>
                  <button className="btn btn-g btn-sm" onClick={()=>setDebugInfo("")}>Dismiss</button>
                </div>
                <pre style={{background:"var(--bark)",color:"#a8c49a",padding:"10px 12px",borderRadius:"var(--rs)",fontSize:10,overflowX:"auto",maxHeight:160,whiteSpace:"pre-wrap",wordBreak:"break-all"}}>{debugInfo}</pre>
              </div>
            )}
          </div>
        )}


        {/* ══════════ PHOTO SCAN SCREEN ══════════ */}
        {tab==="recipes"&&addMode==="photo"&&!editRecipe&&(
          <PhotoScanScreen
            onBack={()=>setAddMode("choice")}
            onScanned={(recipe)=>{
              setEditRecipe(recipe);
              setAddMode("manual");
              toast("📷 Recipe scanned! Review and save below.","success");
            }}
            toast={toast}
          />
        )}

        {/* ══════════ COMMUNITY BROWSER ══════════ */}
        {tab==="recipes"&&addMode==="community"&&(
          <div>
            <div className="sh" style={{display:"flex",alignItems:"center",gap:12}}>
              <button onClick={()=>setAddMode("choice")} style={{background:"none",border:"none",cursor:"pointer",color:"var(--soft)",fontSize:20,lineHeight:1,padding:0}}>←</button>
              <div><h2>Community Recipes</h2><p>Recipes shared by other Platewise users — save any to your library</p></div>
            </div>

            {/* Search + Filter */}
            <div style={{display:"flex",gap:8,marginBottom:10,alignItems:"center"}}>
              <input className="sinput" style={{flex:1}} value={communitySearch} onChange={e=>setCommunitySearch(e.target.value)} placeholder="Search community recipes…"/>
              <select
                value={communitySortBy}
                onChange={e=>setCommunitySortBy(e.target.value)}
                style={{padding:"8px 10px",borderRadius:"var(--rs)",border:"1.5px solid var(--border)",background:"white",fontSize:13,color:"var(--bark)",fontFamily:"'DM Sans',sans-serif",cursor:"pointer"}}
              >
                <option value="match">Ingredients matched</option>
                <option value="time">Cook time</option>
                <option value="alpha">A to Z</option>
              </select>
            </div>

            {communityLoading&&<p style={{textAlign:"center",color:"var(--soft)",padding:40}}>Loading community recipes…</p>}

            {!communityLoading&&(()=>{
              const myRecipeIds=new Set(recipes.map(r=>r.id));
              const filtered=communityRecipes
                .filter(r=>{
                  const s=communitySearch.toLowerCase();
                  return !s||r.title?.toLowerCase().includes(s)||r.cuisine?.toLowerCase().includes(s)||r.tags?.some(t=>t.toLowerCase().includes(s));
                })
                .sort((a,b)=>{
                  // Always push already-saved recipes to bottom
                  const savedA=myRecipeIds.has(a.id)||recipes.some(x=>x.title===a.title);
                  const savedB=myRecipeIds.has(b.id)||recipes.some(x=>x.title===b.title);
                  if(savedA&&!savedB)return 1;
                  if(!savedA&&savedB)return -1;
                  if(communitySortBy==="alpha") return a.title?.localeCompare(b.title||"")||0;
                  if(communitySortBy==="time") return (a.time||0)-(b.time||0);
                  // Default: shared ingredients score
                  const scoreA=a.ingredients?.filter(i=>overlapNames.has(i.name?.toLowerCase())).length||0;
                  const scoreB=b.ingredients?.filter(i=>overlapNames.has(i.name?.toLowerCase())).length||0;
                  return scoreB-scoreA;
                });

              if(!filtered.length) return(
                <div style={{textAlign:"center",padding:"40px 20px",color:"var(--soft)"}}>
                  <div style={{fontSize:40,marginBottom:12}}>🌍</div>
                  <p style={{fontSize:15}}>No community recipes yet.</p>
                  <p style={{fontSize:13,marginTop:4}}>Be the first to share one!</p>
                </div>
              );

              return(
                <div className="grid">
                  {filtered.map(r=>{
                    const alreadySaved=myRecipeIds.has(r.id)||recipes.some(x=>x.title===r.title);
                    const shared=r.ingredients?.filter(i=>overlapNames.has(i.name?.toLowerCase())).length||0;
                    const total=r.ingredients?.length||0;
                    const pct=total>0?shared/total:0;
                    const bg=pct>=0.5?"var(--sage)":pct>0?"var(--gold)":"#e2e8f0";
                    const col=pct>=0.5?"white":pct>0?"white":"#64748b";
                    const hasAllergen=profile?.allergens?.length>0&&r.ingredients?.some(i=>
                      profile.allergens.some(a=>i.name?.toLowerCase().includes(a.toLowerCase()))
                    );
                    const submittedBy=r.profiles?.username;
                    const sharedDate=r.created_at||r.profiles?.created_at
                      ?new Date(r.created_at||r.profiles?.created_at).toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"})
                      :null;
                    return(
                      <div key={r.id} className="card" style={{position:"relative"}}>
                        <span className="badge" style={{background:"#6366f1",color:"white",left:10,right:"auto"}}>🌍 Community</span>
                        {hasAllergen&&<span className="badge" style={{background:"#dc2626",color:"white",right:10,left:"auto"}}>!</span>}
                        <img className="card-img" src={r.image||"https://images.unsplash.com/photo-1495521821757-a1efb6729352?w=400&q=80"} alt={r.title} loading="lazy" onError={e=>e.target.src="https://images.unsplash.com/photo-1495521821757-a1efb6729352?w=400&q=80"}/>
                        <div className="card-body">
                          <div className="card-title">{r.title}</div>
                          <div className="card-meta">
                            <span>⏱ {r.time}m</span>
                            <span>👤 {r.servings}</span>
                            {r.cuisine&&<span>{r.cuisine}</span>}
                            <span style={{background:bg,color:col,fontSize:11,padding:"2px 7px",borderRadius:20,fontWeight:600}}>{shared}/{total} shared</span>
                          </div>
                          {r.tags?.length>0&&<div className="tags">{r.tags.map(t=><span key={t} className="tag">{t}</span>)}</div>}
                          <div style={{fontSize:11,color:"var(--soft)",marginTop:6,display:"flex",gap:6,flexWrap:"wrap"}}>
                            {submittedBy&&<span>👤 @{submittedBy}</span>}
                            {sharedDate&&<span>· {sharedDate}</span>}
                          </div>
                          <div className="card-actions" style={{marginTop:8}}>
                            {alreadySaved
                              ? <span style={{fontSize:12,color:"var(--sage)",fontWeight:600,padding:"6px 0"}}>✓ In your library</span>
                              : <button className="btn btn-p" style={{fontSize:12}} onClick={()=>saveCommunityRecipe(r)}>+ Save to Library</button>
                            }
                            <button className="btn btn-s btn-sm" onClick={()=>{setDetailRecipe(r);setAddMode(null);}}>View</button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        )}


        {/* ══════════ EDIT / ADD RECIPE ══════════ */}
        {editRecipe&&tab==="recipes"&&addMode!=="url"&&(()=>{
          const uid=getCurrentUserId();
          const originalAuthor=editRecipe.user_id||uid;
          const canTogglePublic=!session?.guest&&!!uid&&(!originalAuthor||originalAuthor===uid);
          return(
            <EditPanel
              recipe={editRecipe}
              onSave={saveRecipe}
              onCancel={()=>{setEditRecipe(null);setAddMode(null);}}
              onPhoto={(file)=>handlePhoto(file,editRecipe.id)}
              uploading={uploadingPhoto}
              toast={toast}
              allIngredients={allIngredients}
              isAdmin={isAdmin}
              isManualAdd={addMode==="manual"||(!editRecipe.is_shared&&!editRecipe.source_url&&!editRecipe.import_url&&!editRecipe.imported_url&&!editRecipe.is_imported)}
              canTogglePublic={canTogglePublic}
            />
          );
        })()}

        {/* ══════════ ADD RECIPE TAB ══════════ */}
        {/* ══════════ PLANNER ══════════ */}
        {tab==="planner"&&(<>
          <div className="sh" style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",flexWrap:"wrap",gap:12}}>
            <div><h2>Weekly Meal Planner</h2><p>Click + on any day to assign meals. Navigate weeks with the arrows.</p></div>
            {Object.keys(mealPlan).length>0&&(
              <button className="btn btn-p" style={{flex:"none",marginTop:4}} onClick={()=>setCalModal(true)}>📅 Save to Calendar</button>
            )}
          </div>
          <div className="wnav">
            <button onClick={()=>setWeekOffset(o=>o-1)}>‹</button>
            <h3>{dispDate(weekDates[0])} – {dispDate(weekDates[6])}, {weekDates[0].getFullYear()}</h3>
            <button onClick={()=>setWeekOffset(o=>o+1)}>›</button>
            <button className="btn btn-s btn-sm" onClick={()=>setWeekOffset(0)}>Today</button>
          </div>
          <div className="calgrid">
            {weekDates.map(date=>{
              const ds=fmtDate(date);
              const meals=(mealPlan[ds]||[]).map(id=>recipes.find(r=>r.id===id)).filter(Boolean);
              return(
                <div key={ds} className={`calday ${ds===todayStr?"today":""}`}>
                  <div className="cddl">{DAYS[date.getDay()]}</div>
                  <div className="cddn">{date.getDate()}</div>
                  {meals.map(r=>(
                    <div key={r.id} className="cmeal">
                      <img className="cmeal-img" src={r.image} alt={r.title} onError={e=>e.target.src="https://images.unsplash.com/photo-1495521821757-a1efb6729352?w=400&q=80"}/>
                      <span className="cmeal-t">{r.title.length>20?r.title.slice(0,20)+"…":r.title}</span>
                      <button className="cmeal-rm" onClick={()=>removeFromDay(ds,r.id)}>×</button>
                    </div>
                  ))}
                  <button className="cadd" onClick={()=>{setAddMealModal(ds);setModalSearch("");}}>+ Add</button>
                </div>
              );
            })}
          </div>

          {selected.length>0&&(()=>{
            // Sort selected recipes by shortest shelf-life ingredient (most perishable first)
            const getMinShelfLife=(r)=>r.ingredients?.length
              ? Math.min(...r.ingredients.map(i=>Number(i.shelfDays)||999))
              : 999;
            const sortedSelected=[...selected]
              .map(id=>recipes.find(x=>x.id===id))
              .filter(Boolean)
              .sort((a,b)=>getMinShelfLife(a)-getMinShelfLife(b));
            const mostPerishable=getMinShelfLife(sortedSelected[0]);
            const freshLabel=mostPerishable<=2?"🔴 Cook first — very perishable":mostPerishable<=5?"🟠 Cook early in the week":mostPerishable<=10?"🟡 Cook within a few days":"🟢 Flexible — longer shelf life";
            return(
              <>
                <div className="sh">
                  <div>
                    <h3 style={{fontSize:20,fontFamily:"'Playfair Display',serif",color:"var(--bark)"}}>Selected Meals</h3>
                    <p>Sorted by ingredient freshness — cook the top recipes first</p>
                  </div>
                </div>
                {/* Freshness banner */}
                <div style={{background:"#f0faf4",border:"1px solid #86efac",borderRadius:"var(--r)",padding:"10px 14px",marginBottom:14,display:"flex",alignItems:"center",gap:10,fontSize:13}}>
                  <span style={{fontSize:18}}>🥬</span>
                  <div>
                    <span style={{fontWeight:600,color:"var(--sage)"}}>Freshness order</span>
                    <span style={{color:"var(--soft)",marginLeft:8}}>Recipes are ordered by their shortest shelf-life ingredient. Add them to your planner starting Monday.</span>
                  </div>
                </div>
                <div className="grid">
                  {sortedSelected.map((r,idx)=>{
                    const minDays=getMinShelfLife(r);
                    const perishIng=r.ingredients?.find(i=>Number(i.shelfDays||999)===minDays);
                    const urgency=minDays<=2?{bg:"#fef2f2",color:"#b91c1c",label:"Cook first"}:minDays<=5?{bg:"#fff7ed",color:"#c2410c",label:"Cook early"}:minDays<=10?{bg:"#fefce8",color:"#a16207",label:"Mid-week"}:{bg:"#f0fdf4",color:"#166534",label:"Flexible"};
                    return(
                      <div key={r.id} className="card sel" style={{position:"relative"}}>
                        <span className="badge badge-sel">✓ Selected</span>
                        {/* Freshness rank badge */}
                        <div style={{position:"absolute",top:8,right:8,background:urgency.bg,color:urgency.color,border:`1px solid ${urgency.color}33`,borderRadius:20,fontSize:10,fontWeight:700,padding:"2px 8px",zIndex:2}}>
                          #{idx+1} · {urgency.label}
                        </div>
                        <img className="card-img" src={r.image} alt={r.title} loading="lazy" onError={e=>e.target.src="https://images.unsplash.com/photo-1495521821757-a1efb6729352?w=400&q=80"}/>
                        <div className="card-body">
                          <div className="card-title">{r.title}</div>
                          <div className="card-meta"><span>⏱ {r.time}m</span><span>👤 {servingsOvr[r.id]||r.servings} servings</span></div>
                          {perishIng&&(
                            <div style={{fontSize:11,color:urgency.color,marginTop:4,display:"flex",alignItems:"center",gap:4}}>
                              <span>⏳</span>
                              <span>Use <strong>{perishIng.name}</strong> within {minDays}d</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            );
          })()}

          {!Object.keys(mealPlan).length&&!selected.length&&(
            <div className="empty"><div className="ei">📅</div><h3>Your week is empty</h3><p>Go to Recipes, select some meals, then plan your week here</p>
              <button className="btn btn-p" style={{marginTop:16,display:"inline-flex"}} onClick={()=>navTo("recipes")}>Browse Recipes →</button>
            </div>
          )}
        </>)}

        {/* ══════════ SHOPPING ══════════ */}
        {tab==="shopping"&&(<>
          <div className="sh"><h2>Shopping List</h2><p>Auto-generated from your planner — quantities scale with your servings settings</p></div>

          {!shopList.length?(
            <div className="empty"><div className="ei">🛒</div><h3>Nothing to shop for yet</h3><p>Add meals to your planner and the list appears automatically</p>
              <button className="btn btn-p" style={{marginTop:16,display:"inline-flex"}} onClick={()=>navTo("planner")}>Go to Planner →</button>
            </div>
          ):(<>
            {/* Legend */}
            <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:16}}>
              <span className="dinfo dok">✓ Fresh until meal date</span>
              <span className="dinfo dwarn">⚠ Buy later in the week</span>
              <span className="dinfo durgent">⚡ Very short shelf life</span>
              <div style={{marginLeft:"auto",display:"flex",gap:8}}>
                <button className="btn btn-g btn-sm" onClick={()=>{
                  const allK=shopList.map(i=>i.name.toLowerCase());
                  const allChk=allK.every(k=>checked[k]);
                  const next={};if(!allChk)allK.forEach(k=>next[k]=true);
                  setChecked(next);
                }}>{shopCheckedCount===shopList.length?"Uncheck all":"Check all"}</button>
                <button className="btn btn-g btn-sm" onClick={()=>setChecked({})}>Reset</button>
              </div>
            </div>

            {/* Progress */}
            <div className="prog-wrap">
              <div className="prog-labels">
                <span>{shopCheckedCount} of {shopList.length} items collected</span>
                <span style={{color:"var(--sage)",fontWeight:500}}>{Math.round(shopCheckedCount/shopList.length*100)}%</span>
              </div>
              <div className="prog-bar"><div className="prog-fill" style={{width:`${Math.round(shopCheckedCount/shopList.length*100)}%`}}/></div>
            </div>

            {["produce","meat","fish","dairy","pantry"].map(cat=>{
              const items=shopList.filter(i=>i.category===cat);
              if(!items.length)return null;
              const catChk=items.filter(i=>!!checked[i.name.toLowerCase()]).length;
              return(
                <div key={cat} style={{marginBottom:24}}>
                  <div className="cattitle">
                    <span style={{width:10,height:10,borderRadius:"50%",background:catColor(cat),display:"inline-block"}}/>
                    {cat} <span style={{fontWeight:400,opacity:0.6}}>({catChk}/{items.length})</span>
                  </div>
                  {items.map((item,idx)=>{
                    const key=item.name.toLowerCase().trim();
                    // Check cupboard with normalised key — handle case variations
                    const isInCupboard=Object.keys(cupboard).some(k=>k.toLowerCase().trim()===key&&cupboard[k]);
                    const isChk=!!checked[key]||isInCupboard;
                    return(
                      <div key={idx} className={`shoprow ${isChk?"chk":""}`} onClick={()=>{
                        const nowChecked=!isChk;
                        setChecked(p=>({...p,[key]:nowChecked}));
                        // Sync with cupboard — ticking adds, unticking removes
                        setCupboard(p=>({...p,[key]:nowChecked}));
                        // Also remove any case variants from cupboard
                        if(!nowChecked){
                          setCupboard(p=>{
                            const next={...p};
                            Object.keys(next).forEach(k=>{if(k.toLowerCase().trim()===key)delete next[k];});
                            return next;
                          });
                        }
                      }}>
                        <div className="cbox">{isChk&&"✓"}</div>
                        <div className="idot" style={{background:catColor(item.category)}}/>
                        <div className="iname">{item.name}</div>
                        <div className="iqty">{fmtQty(item.qty)} {item.unit}</div>
                        <span className={`dinfo d${item.dateStatus}`}>
                          {item.dateStatus==="ok"?"✓ In date":item.dateStatus==="warn"?`⚠ Buy within ${item.daysUntilMeal-1}d`:"⚡ Buy day before"}
                        </span>
                        {item.useByDate&&(
                          <span style={{fontSize:10,padding:"2px 7px",borderRadius:20,background:"#f0f7ee",color:"var(--sage)",whiteSpace:"nowrap",flexShrink:0}}>
                            Use by {item.useByDate}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}

            {/* WhatsApp Share Button */}
            <div style={{marginTop:28,paddingTop:20,borderTop:"1.5px solid var(--border)"}}>
              <button
                onClick={()=>{
                  const cats=["produce","meat","fish","dairy","pantry"];
                  const catLabels={produce:"Produce",meat:"Meat",fish:"Fish",dairy:"Dairy",pantry:"Pantry"};
                  let msg="🛒 *Platewise Shopping List*\n\n";
                  cats.forEach(cat=>{
                    const items=shopList.filter(i=>i.category===cat&&!checked[i.name.toLowerCase()]);
                    if(!items.length)return;
                    msg+=`*${catLabels[cat]}*\n`;
                    items.forEach(i=>{
                      msg+=`• ${fmtQty(i.qty)}${i.unit?" "+i.unit:""} ${i.name}`;
                      if(i.useByDate)msg+=` _(use by ${i.useByDate})_`;
                      msg+="\n";
                    });
                    msg+="\n";
                  });
                  const planned=Object.entries(mealPlan).sort(([a],[b])=>a.localeCompare(b));
                  if(planned.length){
                    msg+="*📅 This Week\'s Meals*\n";
                    planned.forEach(([dateStr,ids])=>{
                      const d=new Date(dateStr+"T00:00:00");
                      const label=d.toLocaleDateString("en-GB",{weekday:"short",day:"numeric",month:"short"});
                      ids.forEach(rid=>{
                        const r=recipes.find(x=>x.id===rid);
                        if(r)msg+=`• ${label}: ${r.title}\n`;
                      });
                    });
                  }
                  window.open("https://wa.me/?text="+encodeURIComponent(msg),"_blank");
                }}
                style={{display:"flex",alignItems:"center",justifyContent:"center",gap:10,width:"100%",padding:"14px 20px",background:"#25D366",color:"white",border:"none",borderRadius:"var(--rs)",fontSize:15,fontWeight:600,fontFamily:"'DM Sans',sans-serif",cursor:"pointer",transition:"background 0.2s",}}
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Share Shopping List via WhatsApp
              </button>
              <p style={{fontSize:11,color:"var(--soft)",textAlign:"center",marginTop:8}}>Unchecked items only · Includes this week's meals</p>
            </div>
          </>)}


        </>)}

        {/* ══════════ CUPBOARD ══════════ */}
        {tab==="cupboard"&&(<>
          <div className="sh"><h2>🧺 Cupboard</h2><p>Items in your cupboard count as shared ingredients across all recipes. Tick items in your shopping list to add them automatically.</p></div>

          {/* Search to add */}
          <div style={{marginBottom:20}}>
            <div style={{position:"relative"}}>
              <input
                className="sinput"
                style={{width:"100%",borderRadius:"var(--rs)",padding:"10px 16px"}}
                value={cupboardSearch}
                onChange={e=>setCupboardSearch(e.target.value)}
                placeholder="Search ingredients to add to your cupboard…"
              />
              {cupboardSearch.length>=1&&(()=>{
                const suggestions=allIngredients.filter(n=>
                  n.toLowerCase().includes(cupboardSearch.toLowerCase())&&!cupboard[n.toLowerCase()]
                ).slice(0,8);
                return suggestions.length>0?(
                  <div style={{position:"absolute",top:"calc(100% + 4px)",left:0,right:0,zIndex:100,background:"white",border:"1.5px solid var(--sage)",borderRadius:"var(--rs)",boxShadow:"0 4px 16px rgba(0,0,0,0.12)",overflow:"hidden"}}>
                    {suggestions.map((s,i)=>(
                      <div key={i}
                        onMouseDown={e=>{e.preventDefault();setCupboard(p=>({...p,[s.toLowerCase()]:true}));setCupboardSearch("");}}
                        style={{padding:"10px 14px",fontSize:14,cursor:"pointer",color:"var(--bark)",borderBottom:i<suggestions.length-1?"1px solid var(--border)":"none",display:"flex",alignItems:"center",gap:8}}
                      >
                        <span style={{fontSize:16}}>+</span> {s}
                      </div>
                    ))}
                  </div>
                ):null;
              })()}
            </div>
            <p style={{fontSize:11,color:"var(--soft)",marginTop:6}}>Or tick items on your Shopping List — they'll appear here automatically</p>
          </div>

          {/* Cupboard items */}
          {Object.keys(cupboard).filter(k=>cupboard[k]).length===0?(
            <div className="empty"><div className="ei">🧺</div><h3>Your cupboard is empty</h3><p>Search for ingredients above or tick items on your shopping list</p></div>
          ):(
            <>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
                <p style={{fontSize:14,color:"var(--soft)"}}>{Object.keys(cupboard).filter(k=>cupboard[k]).length} items in your cupboard</p>
                <button className="btn btn-g btn-sm" onClick={()=>{if(confirm("Clear all cupboard items?")){setCupboard({});setChecked({});}}}>Clear all</button>
              </div>
              {(()=>{
                const catOrder=["produce","meat","fish","dairy","pantry"];
                const catLabels={produce:"Produce",meat:"Meat",fish:"Fish",dairy:"Dairy",pantry:"Pantry"};
                const items=Object.keys(cupboard).filter(k=>cupboard[k]).sort();
                // Group by category
                const grouped={};
                items.forEach(name=>{
                  const r=recipes.find(rec=>rec.ingredients?.some(i=>i.name.toLowerCase()===name));
                  const ing=r?.ingredients?.find(i=>i.name.toLowerCase()===name);
                  const cat=ing?.category||"pantry";
                  if(!grouped[cat])grouped[cat]=[];
                  grouped[cat].push(name);
                });
                return catOrder.filter(cat=>grouped[cat]?.length>0).map(cat=>(
                  <div key={cat} style={{marginBottom:20}}>
                    <div style={{fontSize:12,fontWeight:600,color:"var(--soft)",textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:8}}>
                      {catLabels[cat]} <span style={{fontWeight:400,color:"var(--soft)"}}>({grouped[cat].length})</span>
                    </div>
                    <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
                      {grouped[cat].map(name=>(
                        <div key={name} style={{display:"flex",alignItems:"center",gap:6,background:"#f0faf0",border:"1.5px solid var(--sage-light)",borderRadius:20,padding:"6px 12px 6px 14px",fontSize:13,color:"var(--bark)"}}>
                          <span style={{color:"var(--sage)",fontWeight:600,fontSize:12}}>✓</span>
                          <span style={{fontWeight:500}}>{name.charAt(0).toUpperCase()+name.slice(1)}</span>
                          <button
                            onClick={()=>{setCupboard(p=>({...p,[name]:false}));setChecked(p=>({...p,[name]:false}));}}
                            style={{background:"none",border:"none",color:"var(--soft)",cursor:"pointer",fontSize:16,lineHeight:1,padding:"0 0 0 4px",display:"flex",alignItems:"center"}}
                          >×</button>
                        </div>
                      ))}
                    </div>
                  </div>
                ));
              })()}

              {/* Show which recipes are fully covered by cupboard only */}
              {(()=>{
                const fullyIn=recipes.filter(r=>{
                  const total=r.ingredients.length;
                  if(!total)return false;
                  const shared=r.ingredients.filter(i=>cupboardIngNames.has(i.name.toLowerCase())).length;
                  return shared===total;
                });
                return fullyIn.length>0?(
                  <div style={{marginTop:24,padding:16,background:"#f0faf0",border:"1.5px solid var(--sage-light)",borderRadius:"var(--r)"}}>
                    <h4 style={{fontSize:14,color:"var(--sage)",marginBottom:8,fontFamily:"'Playfair Display',serif"}}>✨ Recipes you can make right now</h4>
                    <p style={{fontSize:12,color:"var(--soft)",marginBottom:10}}>All ingredients are in your cupboard or shopping list</p>
                    {fullyIn.map(r=>(
                      <div key={r.id} style={{display:"flex",alignItems:"center",gap:10,marginBottom:8,cursor:"pointer"}} onClick={()=>{setDetailRecipe(r);setTab("recipes");}}>
                        <img src={r.image} style={{width:40,height:40,borderRadius:8,objectFit:"cover"}} onError={e=>e.target.style.display="none"}/>
                        <span style={{fontSize:14,fontWeight:500,color:"var(--bark)"}}>{r.title}</span>
                      </div>
                    ))}
                  </div>
                ):null;
              })()}
            </>
          )}
        </>)}

        {/* ══════════ SETTINGS ══════════ */}
        {tab==="settings"&&(<>
          <div className="sh"><h2>Settings</h2><p>Manage your Platewise account and data</p></div>

          {/* Account info */}
          <div className="sbanner">
            <h4>👤 Account</h4>
            {session&&!session.guest?(
              <>
                <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:12}}>
                  <div style={{width:48,height:48,borderRadius:"50%",background:"var(--warm)",border:"1.5px solid var(--border)",display:"flex",alignItems:"center",justifyContent:"center",overflow:"hidden",flexShrink:0}}>
                    {(()=>{
                      if((profile?.avatar_type==="food-photo"||profile?.avatar_type==="upload")&&profile?.avatar_url)
                        return <img src={profile.avatar_url} style={{width:"100%",height:"100%",objectFit:"cover"}}/>;
                      const illus=FOOD_ILLUSTRATIONS.find(([k])=>k===(profile?.avatar_illustration||"person1"));
                      const av=illus?.[1];
                      return <div style={{width:"100%",height:"100%",background:av?.bg||"#f5f0e8",display:"flex",alignItems:"center",justifyContent:"center",fontSize:24}}>{av?.emoji||"👤"}</div>;
                    })()}
                  </div>
                  <div>
                    {profile
                      ? <><div style={{fontWeight:600,color:"var(--bark)"}}>{profile.first_name} {isAdmin&&<span style={{fontSize:11,background:"rgba(99,102,241,0.12)",color:"#6366f1",padding:"1px 7px",borderRadius:10,marginLeft:4}}>⭐ Admin</span>}</div>
                          <div style={{fontSize:13,color:"var(--soft)"}}>@{profile.username}</div></>
                      : <div style={{fontSize:13,color:"var(--soft)"}}>{session.user?.email}</div>
                    }
                  </div>
                </div>
                <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                  <button className="btn btn-s btn-sm" onClick={()=>setShowProfileModal(true)}>Edit Profile</button>
                  <button className="btn btn-del btn-sm" onClick={handleSignOut}>Sign Out</button>
                </div>
              </>
            ):(
              <>
                <p style={{marginBottom:10}}>Sign in with Google or create an account to sync your recipes across devices.</p>
                {dbConn&&<p style={{marginTop:10,color:"var(--sage)",fontWeight:500}}>✅ Connected to Platewise</p>}
              </>
            )}
          </div>

          {/* Admin-only: data management */}
          {isAdmin&&!session?.guest&&(
            <div style={{marginBottom:28}}>
              <h3 style={{fontSize:18,marginBottom:12,color:"var(--bark)"}}>🛠 Data Management</h3>
              <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
                <button className="btn btn-s" onClick={()=>{
                  const blob=new Blob([JSON.stringify({recipes,mealPlan},null,2)],{type:"application/json"});
                  const a=document.createElement("a");a.href=URL.createObjectURL(blob);a.download="platewise-backup.json";a.click();
                  toast("📦 Backup downloaded","success");
                }}>📦 Export Backup</button>
                <label className="btn btn-s" style={{cursor:"pointer"}}>
                  📂 Import Backup
                  <input type="file" accept=".json" style={{display:"none"}} onChange={e=>{
                    const f=e.target.files[0];if(!f)return;
                    const r=new FileReader();
                    r.onload=ev=>{try{const{recipes:rcp,mealPlan:mp}=JSON.parse(ev.target.result);if(rcp)setRecipes(rcp);if(mp)setMealPlan(mp);toast("📂 Backup restored","success");}catch{toast("Invalid backup file","error");}};
                    r.readAsText(f);e.target.value="";
                  }}/>
                </label>
                <button className="btn btn-del" onClick={()=>{if(confirm("Reset ALL data? This cannot be undone.")){setRecipes([]);setMealPlan({});setSelected([]);setChecked({});setServingsOvr({});toast("Data reset to defaults","info");}}}>🗑 Reset All Data</button>
              </div>
            </div>
          )}

          {session&&!session.guest&&<DeleteAccountSection onDelete={handleDeleteAccount}/>}

          <div>
            <h3 style={{fontSize:18,marginBottom:12,color:"var(--bark)"}}>Stats</h3>
            <div className="statgrid">
              {[["📖","Recipes",recipes.length],["📅","Meals Planned",Object.values(mealPlan).reduce((a,b)=>a+b.length,0)],["🛒","Shopping Items",shopList.length],["✓","Items Checked",shopCheckedCount],].map(([icon,label,val])=>(
                <div key={label} className="statcard"><div className="stn">{icon}</div><div className="stv">{val}</div><div className="stl">{label}</div></div>
              ))}
            </div>
          </div>
        </>)}

      {/* ── Global footer ── */}
      <div style={{borderTop:"1px solid var(--border)",padding:"16px 20px 80px",display:"flex",flexWrap:"wrap",gap:"8px 16px",alignItems:"center",justifyContent:"space-between"}}>
        <div style={{display:"flex",gap:16,flexWrap:"wrap"}}>
          <a href="/public/platewise-privacy.html" target="_blank" style={{fontSize:12,color:"var(--soft)",textDecoration:"none"}}>Privacy Policy</a>
          <a href="/public/platewise-terms.html" target="_blank" style={{fontSize:12,color:"var(--soft)",textDecoration:"none"}}>Terms of Service</a>
          <a href="/public/platewise-acceptable-use.html" target="_blank" style={{fontSize:12,color:"var(--soft)",textDecoration:"none"}}>Acceptable Use</a>
        </div>
        <p style={{fontSize:11,color:"var(--soft)"}}>Platewise v2 · © 2026 Platewise</p>
      </div>

      </main>

      {/* Mobile nav */}
      <div className="mobnav">
        {[["recipes","📖","Recipes"],["planner","📅","Planner"],["shopping","🛒","Shopping"],["cupboard","🧺","Cupboard"],["settings","⚙","Settings"]].map(([id,icon,label])=>(
          <button key={id} className={`mtab ${tab===id?"active":""}`} onClick={()=>navTo(id)}>
            <span className="micon">{icon}</span>{label}
          </button>
        ))}
      </div>
    </div>

    {/* Hidden file input for photo on detail page */}
    <input type="file" accept="image/*" ref={photoRef} style={{display:"none"}}
      onChange={e=>{const f=e.target.files[0];if(!f)return;const rid=Number(e.target.dataset.rid)||(detailRecipe?.id);if(rid)handlePhoto(f,rid);e.target.value="";}}/>

    {/* Recipe Review Modal */}
    {pendingRecipe&&(
      <ReviewModal
        recipe={pendingRecipe}
        onConfirm={async(r,makeShared)=>{
          const normalised={...r,ingredients:r.ingredients.map(normaliseIngredient)};
          setRecipes(p=>[...p,normalised]);
          await pushRecipe(normalised, makeShared);
          setPendingRecipe(null);
          setImportUrl("");
          setShareToAll(false);
          setTab("recipes");
          toast(`✅ "${normalised.title}" ${makeShared?"added as a featured recipe!":"saved to your library!"}`, "success");
        }}
        onDiscard={()=>{setPendingRecipe(null);toast("Recipe discarded","info");}}
        normalise={normaliseIngredient}
        allIngredients={allIngredients}
        isAdmin={isAdmin}
        shareToAll={shareToAll}
        setShareToAll={setShareToAll}
      />
    )}

    {/* Profile Modal */}
    {showProfileModal&&(
      <ProfileModal
        profile={profile}
        session={session}
        isAdmin={isAdmin}
        loading={authLoading}
        setLoading={setAuthLoading}
        onClose={()=>setShowProfileModal(false)}
        onSave={(p)=>{setProfile(p);setShowProfileModal(false);toast("Profile updated!","success");}}
        onDeleteAccount={async()=>{await handleDeleteAccount();setShowProfileModal(false);}}
        onSignOut={async()=>{await handleSignOut();setShowProfileModal(false);}}
        onSignUpFromGuest={()=>{setShowProfileModal(false);setSession(null);setAuthMode("signup");}}
        onSetupNow={()=>{setShowProfileModal(false);setNeedsProfileSetup(true);}}

      />
    )}

    {/* Calendar Export Modal */}
    {calModal&&(
      <div className="overlay" onClick={()=>setCalModal(false)}>
        <div className="modal" onClick={e=>e.stopPropagation()} style={{maxWidth:480}}>
          <h3>📅 Save to Calendar</h3>
          <p className="mpara">Export your meal plan to your phone's calendar app. Each recipe becomes a dinner event on the day you planned it, with the full ingredients and method in the description.</p>

          <div style={{background:"#f0f7ee",border:"1.5px solid var(--sage-light)",borderRadius:"var(--rs)",padding:14,marginBottom:16,fontSize:13,color:"var(--bark-mid)",lineHeight:1.6}}>
            <strong>How it works:</strong>
            <ol style={{marginLeft:16,marginTop:6,display:"flex",flexDirection:"column",gap:4}}>
              <li>Tap <strong>Download Calendar File</strong> below</li>
              <li>Open the downloaded <strong>.ics file</strong> on your phone</li>
              <li>Your phone will ask which calendar app to add it to — choose <strong>Google Calendar</strong>, <strong>Apple Calendar</strong> or any other</li>
              <li>Each recipe is added as a 🍽 dinner event on the correct day, with full ingredients and method in the event notes</li>
            </ol>
          </div>

          <div style={{marginBottom:16}}>
            <p style={{fontSize:13,fontWeight:600,color:"var(--bark)",marginBottom:8}}>Meals in your plan:</p>
            {Object.entries(mealPlan).sort(([a],[b])=>a.localeCompare(b)).map(([dateStr,ids])=>{
              const d=new Date(dateStr+'T00:00:00');
              const label=d.toLocaleDateString('en-GB',{weekday:'short',day:'numeric',month:'short'});
              return ids.map(rid=>{
                const r=recipes.find(x=>x.id===rid);
                if(!r)return null;
                return(
                  <div key={rid+dateStr} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 10px",background:"white",borderRadius:"var(--rs)",border:"1.5px solid var(--border)",marginBottom:6}}>
                    <img src={r.image} style={{width:36,height:36,borderRadius:6,objectFit:"cover",flexShrink:0}} onError={e=>e.target.style.display='none'}/>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:13,fontWeight:600,color:"var(--bark)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>🍽 {r.title}</div>
                      <div style={{fontSize:11,color:"var(--soft)"}}>{label} · 7:00 PM · {r.time} mins</div>
                    </div>
                    <button className="btn btn-s btn-sm" style={{flexShrink:0}} onClick={()=>printRecipePdf(r,servingsOvr[r.id]||r.servings)} title="Print recipe card">
                      🖨
                    </button>
                  </div>
                );
              });
            })}
          </div>

          <div style={{marginBottom:12,padding:"10px 14px",background:"#fff9ee",border:"1.5px solid #f0d99a",borderRadius:"var(--rs)",fontSize:12,color:"var(--bark-mid)"}}>
            💡 <strong>Recipe cards:</strong> Tap the 🖨 print icon next to any meal to open a printable recipe card you can save as a PDF from your browser.
          </div>

          <div className="mfoot">
            <button className="btn btn-g" onClick={()=>setCalModal(false)}>Cancel</button>
            <button className="btn btn-p" onClick={()=>{downloadIcs(mealPlan,recipes,servingsOvr);setCalModal(false);toast("📅 Calendar file downloaded!","success");}}>
              ⬇ Download Calendar File (.ics)
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Add Meal Modal */}
    {addMealModal&&(
      <div className="overlay" onClick={()=>setAddMealModal(null)}>
        <div className="modal" onClick={e=>e.stopPropagation()}>
          <h3>Add a Meal</h3>
          <p className="mpara">{addMealModal}</p>
          <input className="sinput" style={{width:"100%",marginBottom:12,borderRadius:8}} placeholder="Filter recipes…" value={modalSearch} onChange={e=>setModalSearch(e.target.value)}/>
          {selected.length===0&&(
            <p style={{fontSize:13,color:"var(--soft)",padding:"12px 0"}}>No recipes selected yet. Go to the Recipes tab and select some meals first.</p>
          )}
          <div className="mlist">
            {recipes.filter(r=>selected.includes(r.id)&&r.title.toLowerCase().includes(modalSearch.toLowerCase())).map(r=>(
              <div key={r.id} className="mitem" onClick={()=>addToDay(addMealModal,r.id)}>
                <img src={r.image} alt={r.title} onError={e=>e.target.src="https://images.unsplash.com/photo-1495521821757-a1efb6729352?w=400&q=80"}/>
                <div><strong>{r.title}</strong><span>⏱ {r.time}m · {r.cuisine} · 👤 {servingsOvr[r.id]||r.servings}</span></div>
              </div>
            ))}
          </div>
          <div className="mfoot"><button className="btn btn-g" onClick={()=>setAddMealModal(null)}>Cancel</button></div>
        </div>
      </div>
    )}

    {/* Toasts */}
    <div className="toasts">{toasts.map(t=><div key={t.id} className={`toast ${t.type}`}>{t.msg}</div>)}</div>
    </>
  );
}


// ─── Review Modal ─────────────────────────────────────────────────────────────
// ─── Profile Setup Screen ────────────────────────────────────────────────────

// ─── Food photo avatars ───────────────────────────────────────────────────────
const FOOD_AVATARS = [
  { id:"pasta", url:"https://images.unsplash.com/photo-1612874742237-6526221588e3?w=120&q=80", label:"Pasta" },
  { id:"salad", url:"https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=120&q=80", label:"Salad" },
  { id:"sushi", url:"https://images.unsplash.com/photo-1579871494447-9811cf80d66c?w=120&q=80", label:"Sushi" },
  { id:"tacos", url:"https://images.unsplash.com/photo-1565299585323-38d6b0865b47?w=120&q=80", label:"Tacos" },
  { id:"pizza", url:"https://images.unsplash.com/photo-1513104890138-7c749659a591?w=120&q=80", label:"Pizza" },
  { id:"curry", url:"https://images.unsplash.com/photo-1565557623262-b51c2513a641?w=120&q=80", label:"Curry" },
  { id:"burger", url:"https://images.unsplash.com/photo-1568901346375-23c9450c58cd?w=120&q=80", label:"Burger" },
  { id:"cake", url:"https://images.unsplash.com/photo-1578985545062-69928b1d9587?w=120&q=80", label:"Cake" },
  { id:"brekkie", url:"https://images.unsplash.com/photo-1533089860892-a7c6f0a88666?w=120&q=80", label:"Breakfast" },
  { id:"ramen", url:"https://images.unsplash.com/photo-1569718212165-3a8278d5f624?w=120&q=80", label:"Ramen" },
  { id:"steak", url:"https://images.unsplash.com/photo-1546964124-0cce460f38ef?w=120&q=80", label:"Steak" },
  { id:"smoothie", url:"https://images.unsplash.com/photo-1553530666-ba11a7da3888?w=120&q=80", label:"Smoothie" },
];

function ProfileSetupScreen({onSave, session, loading, setLoading}){
  const [username, setUsername] = React.useState("");
  const [firstName, setFirstName] = React.useState(session?.user?.user_metadata?.full_name?.split(" ")[0]||"");
  const [dietary, setDietary] = React.useState([]);
  const [allergens, setAllergens] = React.useState([]);
  const [avatarType, setAvatarType] = React.useState("illustration");
  const [avatarIllustration, setAvatarIllustration] = React.useState("person1");
  const [error, setError] = React.useState("");

  const toggleItem = (arr, setArr, item) => setArr(p => p.includes(item) ? p.filter(x=>x!==item) : [...p,item]);

  const handleSave = async() => {
    setError("");
    if(!username.trim()){ setError("Username is required"); return; }
    if(!firstName.trim()){ setError("First name is required"); return; }
    if(!/^[a-zA-Z0-9_]+$/.test(username)){ setError("Username can only contain letters, numbers and underscores"); return; }
    setLoading(true);
    try {
      const token = _SB_TOKEN||session?.access_token;
      // Use uid from session only if it's a real UUID (not empty string)
      const sessionUid = session?.user?.id;
      let uid = (sessionUid && sessionUid.length > 10) ? sessionUid : null;
      if(!uid && token){
        try{ uid = JSON.parse(atob(token.split('.')[1])).sub; }catch(e){}
      }
      if(!uid){ setError("Could not identify your account. Please sign out and back in."); setLoading(false); return; }
      const profileData = {
        user_id: uid,
        username: username.toLowerCase().trim(),
        first_name: firstName.trim(),
        avatar_type: avatarType,
        avatar_illustration: avatarIllustration,
        avatar_url: null,
        dietary,
        allergens,
      };
      await sbSaveProfile(profileData, token);
      onSave(profileData);
    } catch(e) {
      // Catch unique constraint violation for username
      const msg = e.message||"";
      if(msg.includes("unique") || msg.includes("duplicate") || msg.includes("profiles_username_key")){
        setError("That username is already taken — please choose another.");
      } else {
        setError(msg||"Something went wrong. Please try again.");
      }
    }
    setLoading(false);
  };

  const setupCss = `
    .setup-wrap{min-height:100vh;background:linear-gradient(145deg,#2d1f14,#3d2a1a);display:flex;align-items:flex-start;justify-content:center;padding:24px 16px;font-family:'DM Sans',sans-serif;overflow-y:auto;}
    .setup-card{background:#faf7f2;border-radius:20px;padding:32px 28px;max-width:480px;width:100%;margin:auto;box-shadow:0 32px 80px rgba(0,0,0,0.5);}
    .setup-logo{font-family:'Playfair Display',serif;font-size:26px;font-weight:700;color:#2d1f14;margin-bottom:4px;}
    .setup-logo span{color:#c4674a;}
    .setup-sub{font-size:13px;color:#7a6256;margin-bottom:24px;}
    .setup-title{font-family:'Playfair Display',serif;font-size:22px;color:#2d1f14;margin-bottom:6px;}
    .setup-hint{font-size:13px;color:#7a6256;margin-bottom:24px;}
    .setup-label{font-size:11px;font-weight:600;color:#7a6256;text-transform:uppercase;letter-spacing:0.6px;display:block;margin-bottom:5px;margin-top:16px;}
    .setup-input{width:100%;padding:11px 14px;border-radius:10px;border:1.5px solid #e8ddd4;font-family:'DM Sans',sans-serif;font-size:14px;outline:none;background:#faf7f2;color:#2d1f14;}
    .setup-input:focus{border-color:#c4674a;background:white;}
    .setup-chips{display:flex;flex-wrap:wrap;gap:6px;margin-top:8px;}
    .setup-chip{padding:6px 12px;border-radius:20px;border:1.5px solid #e8ddd4;background:white;font-size:13px;cursor:pointer;color:#5c3d2e;font-family:'DM Sans',sans-serif;transition:all 0.15s;}
    .setup-chip.on{background:#c4674a;border-color:#c4674a;color:white;}
    .setup-avatar-grid{display:grid;grid-template-columns:repeat(6,1fr);gap:8px;margin-top:8px;}
    .setup-avi{width:100%;aspect-ratio:1;border-radius:12px;border:2px solid #e8ddd4;background:white;display:flex;align-items:center;justify-content:center;font-size:22px;cursor:pointer;transition:all 0.15s;}
    .setup-avi.on{border-color:#c4674a;background:#fff5f3;}
    .setup-btn{width:100%;padding:13px;background:#c4674a;color:white;border:none;border-radius:10px;font-family:'DM Sans',sans-serif;font-size:15px;font-weight:600;cursor:pointer;margin-top:24px;}
    .setup-btn:disabled{opacity:0.55;cursor:not-allowed;}
    .setup-err{background:#fee2e2;color:#b91c1c;border-radius:8px;padding:10px 14px;font-size:13px;margin-top:12px;}
  `;

  return(
    <>
    <style>{setupCss}</style>
    <div className="setup-wrap">
      <div className="setup-card">
        <div className="setup-logo">plate<span>wise</span></div>
        <div className="setup-sub">Welcome! Lets set up your profile.</div>
        <h2 className="setup-title">Create your profile</h2>
        <p className="setup-hint">This takes just a moment. Your username will be visible to others.</p>

        {error&&<div className="setup-err">{error}</div>}

        <label className="setup-label">Username <span style={{color:"#c4674a"}}>*</span></label>
        <input className="setup-input" value={username} onChange={e=>setUsername(e.target.value.replace(/[^a-zA-Z0-9_]/g,""))} placeholder="e.g. sarah_cooks" maxLength={30} autoCapitalize="none" autoComplete="off"/>
        <div style={{fontSize:11,color:"#b8afa8",marginTop:4}}>Letters, numbers and underscores only. Cannot be changed later.</div>

        <label className="setup-label">First name <span style={{color:"#c4674a"}}>*</span></label>
        <input className="setup-input" value={firstName} onChange={e=>setFirstName(e.target.value)} placeholder="e.g. Sarah" maxLength={50}/>

        <label className="setup-label">Profile picture</label>
        <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:8,marginTop:8}}>
          {FOOD_ILLUSTRATIONS.map(([key,av])=>(
            <div key={key}
              onClick={()=>{setAvatarIllustration(key);setAvatarType("illustration");}}
              style={{borderRadius:12,border:`2px solid ${avatarType==="illustration"&&avatarIllustration===key?"#c4674a":"#e8ddd4"}`,
                cursor:"pointer",aspectRatio:"1",
                background:av?.bg||"#f5f0e8",
                display:"flex",alignItems:"center",justifyContent:"center",
                fontSize:28,transition:"border-color 0.15s"}}
            >
              {av?.emoji||"👤"}
            </div>
          ))}
        </div>

        <label className="setup-label">Dietary requirements <span style={{fontSize:11,fontWeight:400}}>(optional)</span></label>
        <div className="setup-chips">
          {DIETARY_OPTIONS.map(d=>(
            <button key={d} className={`setup-chip ${dietary.includes(d)?"on":""}`} onClick={()=>toggleItem(dietary,setDietary,d)}>{d}</button>
          ))}
        </div>

        <label className="setup-label">Allergens <span style={{fontSize:11,fontWeight:400}}>(optional)</span></label>
        <div className="setup-chips">
          {ALLERGEN_OPTIONS.map(a=>(
            <button key={a} className={`setup-chip ${allergens.includes(a)?"on":""}`} onClick={()=>toggleItem(allergens,setAllergens,a)}>{a}</button>
          ))}
        </div>

        <button className="setup-btn" onClick={handleSave} disabled={loading}>
          {loading?"Setting up your account...":"Complete Profile"}
        </button>
      </div>
    </div>
    </>
  );
}


// ─── Auth Screen ──────────────────────────────────────────────────────────────
function AuthScreen({mode,setMode,email,setEmail,password,setPassword,confirm,setConfirm,loading,error,msg,onSignIn,onSignUp,onReset,onClearMsg,onClearErr,sbUrl,setSbUrl,sbKey,setSbKey,onSkip}){

  const authCss=`
    @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700&family=DM+Sans:wght@300;400;500&display=swap');
    .auth-wrap{min-height:100vh;background:linear-gradient(145deg,#2d1f14 0%,#3d2a1a 50%,#2d1f14 100%);display:flex;align-items:center;justify-content:center;padding:20px;font-family:'DM Sans',sans-serif;}
    .auth-card{background:#faf7f2;border-radius:20px;padding:36px 32px;max-width:420px;width:100%;box-shadow:0 32px 80px rgba(0,0,0,0.5);}
    .auth-logo{font-family:'Playfair Display',serif;font-size:34px;font-weight:700;color:#2d1f14;margin-bottom:2px;letter-spacing:-0.5px;}
    .auth-logo span{color:#c4674a;}
    .auth-tagline{font-size:13px;color:#7a6256;margin-bottom:28px;}
    .auth-tabs{display:flex;gap:0;margin-bottom:24px;background:#f0ebe4;border-radius:10px;padding:3px;}
    .auth-tab{flex:1;padding:8px;border:none;border-radius:8px;font-family:'DM Sans',sans-serif;font-size:13px;font-weight:500;cursor:pointer;transition:all 0.2s;background:transparent;color:#7a6256;}
    .auth-tab.on{background:white;color:#2d1f14;box-shadow:0 1px 4px rgba(0,0,0,0.1);}
    .auth-field{margin-bottom:14px;}
    .auth-label{font-size:11px;font-weight:600;color:#7a6256;text-transform:uppercase;letter-spacing:0.6px;display:block;margin-bottom:5px;}
    .auth-input{width:100%;padding:11px 14px;border-radius:10px;border:1.5px solid #e8ddd4;font-family:'DM Sans',sans-serif;font-size:14px;outline:none;background:#faf7f2;color:#2d1f14;transition:border-color 0.15s;}
    .auth-input:focus{border-color:#c4674a;background:white;}
    .auth-input::placeholder{color:#b8afa8;}
    .auth-btn{width:100%;padding:13px;background:#c4674a;color:white;border:none;border-radius:10px;font-family:'DM Sans',sans-serif;font-size:15px;font-weight:600;cursor:pointer;transition:background 0.2s;margin-top:6px;}
    .auth-btn:hover:not(:disabled){background:#b05a3f;}
    .auth-btn:disabled{opacity:0.55;cursor:not-allowed;}
    .auth-error{background:#fee2e2;color:#b91c1c;border-radius:10px;padding:10px 14px;font-size:13px;margin-bottom:14px;border:1px solid #fca5a5;}
    .auth-msg{background:#dcfce7;color:#15803d;border-radius:10px;padding:10px 14px;font-size:13px;margin-bottom:14px;border:1px solid #86efac;}
    .auth-link{background:none;border:none;color:#c4674a;cursor:pointer;font-weight:600;font-family:'DM Sans',sans-serif;font-size:13px;padding:0;text-decoration:underline;}
    .auth-or{text-align:center;margin:16px 0;font-size:12px;color:#b8afa8;display:flex;align-items:center;gap:10px;}
    .auth-or::before,.auth-or::after{content:"";flex:1;height:1px;background:#e8ddd4;}
    .auth-guest{width:100%;padding:11px;background:transparent;color:#7a6256;border:1.5px solid #e8ddd4;border-radius:10px;font-family:'DM Sans',sans-serif;font-size:14px;cursor:pointer;transition:all 0.2s;}
    .auth-guest:hover{border-color:#7a6256;color:#2d1f14;background:#f5efe6;}
    .auth-setup{margin-top:20px;padding:14px;background:#f5efe6;border-radius:10px;border:1px solid #e8ddd4;}
    .auth-setup summary{font-size:12px;color:#7a6256;cursor:pointer;font-weight:500;}
    .auth-setup-inner{margin-top:10px;display:flex;flex-direction:column;gap:8px;}
  `;

  const handleKey=e=>{
    if(e.key==="Enter"){
      if(mode==="signin")onSignIn();
      else if(mode==="signup")onSignUp();
      else onReset();
    }
  };

  return(
    <>
    <style>{authCss}</style>
    <div className="auth-wrap">
      <div className="auth-card">
        <div className="auth-logo">plate<span>wise</span></div>
        <div className="auth-tagline">Your smart household meal planner</div>

        {/* Sign in / Sign up tabs */}
        {mode!=="reset"&&(
          <div className="auth-tabs">
            <button className={`auth-tab ${mode==="signin"?"on":""}`} onClick={()=>{setMode("signin");onClearErr();onClearMsg();}}>Sign In</button>
            <button className={`auth-tab ${mode==="signup"?"on":""}`} onClick={()=>{setMode("signup");onClearErr();onClearMsg();}}>Create Account</button>
          </div>
        )}

        {mode==="reset"&&(
          <div style={{marginBottom:20}}>
            <button className="auth-link" onClick={()=>{setMode("signin");onClearErr();onClearMsg();}}>← Back to sign in</button>
            <h3 style={{fontSize:18,color:"#2d1f14",marginTop:8,fontFamily:"'Playfair Display',serif"}}>Reset your password</h3>
          </div>
        )}

        {error&&<div className="auth-error">⚠ {error}</div>}
        {msg&&<div className="auth-msg">{msg}</div>}

        <div className="auth-field">
          <label className="auth-label">Email address</label>
          <input className="auth-input" type="email" value={email} onChange={e=>setEmail(e.target.value)} onKeyDown={handleKey} placeholder="you@example.com" autoComplete="email"/>
        </div>

        {mode!=="reset"&&(
          <div className="auth-field">
            <label className="auth-label">Password</label>
            <input className="auth-input" type="password" value={password} onChange={e=>setPassword(e.target.value)} onKeyDown={handleKey} placeholder={mode==="signup"?"At least 6 characters":"Your password"} autoComplete={mode==="signup"?"new-password":"current-password"}/>
          </div>
        )}

        {mode==="signup"&&(
          <div className="auth-field">
            <label className="auth-label">Confirm password</label>
            <input className="auth-input" type="password" value={confirm} onChange={e=>setConfirm(e.target.value)} onKeyDown={handleKey} placeholder="Repeat password" autoComplete="new-password"/>
          </div>
        )}

        <button className="auth-btn" onClick={mode==="signin"?onSignIn:mode==="signup"?onSignUp:onReset} disabled={loading}>
          {loading?"Please wait…":mode==="signin"?"Sign In":mode==="signup"?"Create Account":"Send Reset Email"}
        </button>

        {mode==="signin"&&(
          <div style={{textAlign:"center",marginTop:12,fontSize:13,color:"#7a6256"}}>
            <button className="auth-link" onClick={()=>{setMode("reset");onClearErr();onClearMsg();}}>Forgot your password?</button>
          </div>
        )}

        {/* Google Sign In */}
        {sbUrl&&sbKey&&(
          <>
            <div className="auth-or">or</div>
            <button
              onClick={()=>sbSignInWithGoogle()}
              style={{width:"100%",padding:"11px 16px",background:"white",color:"#3c4043",border:"1.5px solid #dadce0",borderRadius:10,fontFamily:"'DM Sans',sans-serif",fontSize:14,fontWeight:500,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:10,transition:"box-shadow 0.2s"}}
              onMouseOver={e=>e.currentTarget.style.boxShadow="0 2px 8px rgba(0,0,0,0.15)"}
              onMouseOut={e=>e.currentTarget.style.boxShadow="none"}
            >
              <svg width="18" height="18" viewBox="0 0 48 48">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                <path fill="none" d="M0 0h48v48H0z"/>
              </svg>
              Sign in with Google
            </button>
          </>
        )}

        <div className="auth-or">or</div>

        <button className="auth-guest" onClick={onSkip}>
          Continue as guest
        </button>

        <div style={{marginTop:16,textAlign:"center",fontSize:11,color:"#b8afa8",lineHeight:1.8}}>
          By continuing you agree to our{" "}
          <a href="platewise-terms.html" target="_blank" style={{color:"#c4674a",textDecoration:"none"}}>Terms</a>
          {" "}and{" "}
          <a href="platewise-privacy.html" target="_blank" style={{color:"#c4674a",textDecoration:"none"}}>Privacy Policy</a>
        </div>

      </div>
    </div>
    </>
  );
}



// ─── Profile Modal (view/edit profile) ───────────────────────────────────────
function ProfileModal({profile, session, isAdmin, onSave, onClose, onDeleteAccount, onSignOut, onSignUpFromGuest, onSetupNow, loading, setLoading}){
  // Guest users see an account prompt from the profile circle.
  if(session?.guest) return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",zIndex:300,display:"flex",alignItems:"center",justifyContent:"center",padding:20,backdropFilter:"blur(4px)"}}>
      <div style={{background:"#faf7f2",borderRadius:20,padding:32,maxWidth:360,width:"100%",textAlign:"center",boxShadow:"0 24px 60px rgba(0,0,0,0.35)"}}>
        <div style={{fontSize:48,marginBottom:12}}>👨‍🍳</div>
        <h3 style={{fontFamily:"'Playfair Display',serif",fontSize:20,color:"var(--bark)",marginBottom:8}}>Guest profile</h3>
        <p style={{fontSize:14,color:"var(--soft)",marginBottom:20}}>Create an account to save your recipes, meal plans and settings across devices.</p>
        <div style={{display:"flex",gap:8,justifyContent:"center",flexWrap:"wrap"}}>
          <button className="btn btn-g" onClick={onClose}>Close</button>
          <button className="btn btn-p" onClick={onSignUpFromGuest}>Register / Sign up</button>
        </div>
      </div>
    </div>
  );

  // If no profile yet, nudge user to complete setup
  if(!profile) return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",zIndex:300,display:"flex",alignItems:"center",justifyContent:"center",padding:20,backdropFilter:"blur(4px)"}}>
      <div style={{background:"#faf7f2",borderRadius:20,padding:32,maxWidth:360,width:"100%",textAlign:"center",boxShadow:"0 24px 60px rgba(0,0,0,0.35)"}}>
        <div style={{fontSize:48,marginBottom:12}}>👨‍🍳</div>
        <h3 style={{fontFamily:"'Playfair Display',serif",fontSize:20,color:"var(--bark)",marginBottom:8}}>Complete your profile</h3>
        <p style={{fontSize:14,color:"var(--soft)",marginBottom:20}}>Add your username and first name to personalise your Platewise experience.</p>
        <div style={{display:"flex",gap:8,justifyContent:"center"}}>
          <button className="btn btn-g" onClick={onClose}>Later</button>
          <button className="btn btn-p" onClick={onSetupNow}>Set up now</button>
        </div>
      </div>
    </div>
  );
  const [editing, setEditing] = React.useState(false);
  const [firstName, setFirstName] = React.useState(profile?.first_name||"");
  const [dietary, setDietary] = React.useState(profile?.dietary||[]);
  const [allergens, setAllergens] = React.useState(profile?.allergens||[]);
  const [avatarType, setAvatarType] = React.useState(profile?.avatar_type||"illustration");
  const [avatarIllustration, setAvatarIllustration] = React.useState(profile?.avatar_illustration||"chef");
  const [avatarUrl, setAvatarUrl] = React.useState(profile?.avatar_url||"");
  const [avatarTab, setAvatarTab] = React.useState("emoji");
  const [error, setError] = React.useState("");
  const [confirmDelete, setConfirmDelete] = React.useState(false);
  const fileRef = React.useRef(null);

  const toggleItem = (arr, setArr, item) => setArr(p => p.includes(item) ? p.filter(x=>x!==item) : [...p,item]);

  const memberSince = profile?.created_at
    ? new Date(profile.created_at).toLocaleDateString("en-GB",{month:"long",year:"numeric"})
    : "Unknown";

  const getAvatarDiv=(key,size=40)=>{
    const illus=FOOD_ILLUSTRATIONS.find(([k])=>k===(key||"person1"));
    const av=illus?.[1];
    return <div style={{width:"100%",height:"100%",background:av?.bg||"#f5f0e8",display:"flex",alignItems:"center",justifyContent:"center",fontSize:size}}>{av?.emoji||"👤"}</div>;
  };

  const getAvatar = () => {
    if((profile?.avatar_type==="food-photo"||profile?.avatar_type==="upload")&&profile?.avatar_url)
      return <img src={profile.avatar_url} style={{width:"100%",height:"100%",objectFit:"cover"}}/>;
    return getAvatarDiv(profile?.avatar_illustration,40);
  };

  const getAvatarPreview = () => {
    if((avatarType==="food-photo"||avatarType==="upload")&&avatarUrl)
      return <img src={avatarUrl} style={{width:"100%",height:"100%",objectFit:"cover"}}/>;
    return getAvatarDiv(avatarIllustration,40);
  };

  const handleSave = async() => {
    if(!firstName.trim()){ setError("First name is required"); return; }
    setLoading(true);
    try {
      const updated = {
        ...profile,
        first_name: firstName.trim(),
        avatar_type: avatarType,
        avatar_illustration: avatarIllustration,
        avatar_url: (avatarType==="upload"||avatarType==="food-photo") ? avatarUrl : null,
        dietary,
        allergens,
      };
      await sbSaveProfile(updated, _SB_TOKEN||session?.access_token);
      onSave(updated);
      setEditing(false);
    } catch(e) {
      setError(e.message||"Save failed");
    }
    setLoading(false);
  };

  const pmCss = `
    .pm-overlay{position:fixed;inset:0;background:rgba(0,0,0,0.55);z-index:300;display:flex;align-items:flex-start;justify-content:center;padding:20px;overflow-y:auto;backdrop-filter:blur(4px);}
    .pm-card{background:#faf7f2;border-radius:20px;padding:28px;max-width:440px;width:100%;margin:auto;box-shadow:0 24px 60px rgba(0,0,0,0.35);}
    .pm-avatar{width:80px;height:80px;border-radius:50%;background:var(--warm);border:3px solid var(--border);display:flex;align-items:center;justify-content:center;overflow:hidden;margin:0 auto 12px;flex-shrink:0;}
    .pm-name{font-family:'Playfair Display',serif;font-size:22px;color:var(--bark);text-align:center;margin-bottom:2px;}
    .pm-username{font-size:13px;color:var(--soft);text-align:center;margin-bottom:4px;}
    .pm-since{font-size:12px;color:var(--soft);text-align:center;margin-bottom:20px;}
    .pm-label{font-size:11px;font-weight:600;color:var(--soft);text-transform:uppercase;letter-spacing:0.5px;margin:16px 0 6px;display:block;}
    .pm-input{width:100%;padding:10px 13px;border-radius:10px;border:1.5px solid var(--border);font-family:'DM Sans',sans-serif;font-size:14px;outline:none;background:white;}
    .pm-input:focus{border-color:var(--terra);}
    .pm-chips{display:flex;flex-wrap:wrap;gap:5px;}
    .pm-chip{padding:5px 10px;border-radius:20px;border:1.5px solid var(--border);background:white;font-size:12px;cursor:pointer;color:var(--bark-mid);font-family:'DM Sans',sans-serif;}
    .pm-chip.on{background:var(--terra);border-color:var(--terra);color:white;}
    .pm-chip.readonly{cursor:default;background:var(--warm);border-color:var(--border);color:var(--soft);}
    .pm-avi-grid{display:grid;grid-template-columns:repeat(6,1fr);gap:6px;margin-top:6px;}
    .pm-avi{width:100%;aspect-ratio:1;border-radius:10px;border:2px solid var(--border);background:white;display:flex;align-items:center;justify-content:center;font-size:18px;cursor:pointer;}
    .pm-avi.on{border-color:var(--terra);background:#fff5f3;}
    .pm-footer{display:flex;gap:8px;margin-top:20px;flex-wrap:wrap;}
    .pm-del-confirm{background:#fee2e2;border-radius:12px;padding:16px;margin-top:12px;font-size:13px;color:#b91c1c;text-align:center;}
    .pm-err{background:#fee2e2;color:#b91c1c;border-radius:8px;padding:8px 12px;font-size:12px;margin-top:8px;}
    .pm-tag{display:inline-flex;align-items:center;padding:3px 10px;border-radius:20px;background:var(--warm);border:1px solid var(--border);font-size:12px;color:var(--soft);}
  `;

  return(
    <>
    <style>{pmCss}</style>
    <div className="pm-overlay" onClick={onClose}>
      <div className="pm-card" onClick={e=>e.stopPropagation()}>

        {/* Avatar */}
        <div className="pm-avatar">
          {editing ? getAvatarPreview() : getAvatar()}
        </div>

        {!editing ? (
          <>
            <div className="pm-name">{profile?.first_name}</div>
            <div className="pm-username">@{profile?.username}</div>
            <div className="pm-since">Member since {memberSince}</div>
            {isAdmin&&(
              <div style={{display:"inline-flex",alignItems:"center",gap:5,background:"rgba(99,102,241,0.12)",border:"1px solid rgba(99,102,241,0.3)",borderRadius:20,padding:"3px 12px",fontSize:12,color:"#6366f1",fontWeight:600,marginBottom:8}}>
                ⭐ Platewise Admin
              </div>
            )}

            {/* Dietary badges */}
            {(profile?.dietary?.length>0||profile?.allergens?.length>0)&&(
              <div style={{marginBottom:16}}>
                <div style={{display:"flex",flexWrap:"wrap",gap:5,justifyContent:"center"}}>
                  {profile.dietary?.map(d=><span key={d} className="pm-tag">{d}</span>)}
                  {profile.allergens?.map(a=><span key={a} className="pm-tag" style={{background:"#fff9ee",borderColor:"#f0d99a",color:"#92400e"}}>{a} allergy</span>)}
                </div>
              </div>
            )}

            <div className="pm-footer">
              <button className="btn btn-g" style={{flex:1}} onClick={onClose}>Close</button>
              <button className="btn btn-s" style={{flex:1}} onClick={()=>setEditing(true)}>Edit Profile</button>
              <button className="btn btn-del" style={{flex:1}} onClick={onSignOut}>Sign out</button>
            </div>
          </>
        ) : (
          <>
            <div style={{textAlign:"center",marginBottom:16}}>
              <div style={{fontSize:13,color:"var(--soft)"}}>Editing profile</div>
              <div style={{fontSize:11,color:"var(--soft)"}}>@{profile?.username} (cannot be changed)</div>
            </div>

            {error&&<div className="pm-err">{error}</div>}

            {/* Avatar edit */}
            <label className="pm-label">Profile picture</label>
            <div style={{display:"flex",gap:4,marginBottom:8,background:"var(--warm)",borderRadius:8,padding:3}}>
              {[["emoji","People"]].map(([id,label])=>(
                <button key={id} onClick={()=>setAvatarTab(id)}
                  style={{flex:1,padding:"5px",border:"none",borderRadius:6,fontSize:12,fontFamily:"'DM Sans',sans-serif",cursor:"pointer",
                    background:avatarTab===id?"white":"transparent",color:avatarTab===id?"var(--bark)":"var(--soft)",
                    fontWeight:avatarTab===id?600:400,boxShadow:avatarTab===id?"0 1px 3px rgba(0,0,0,0.1)":"none",transition:"all 0.15s"}}
                >{label}</button>
              ))}
            </div>
            {avatarTab==="emoji"&&(
              <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:8,marginTop:4}}>
                {FOOD_ILLUSTRATIONS.map(([key,url])=>(
                  <div key={key}
                    onClick={()=>{setAvatarIllustration(key);setAvatarType("illustration");}}
                    style={{borderRadius:12,overflow:"hidden",border:`2px solid ${avatarType==="illustration"&&avatarIllustration===key?"var(--terra)":"var(--border)"}`,
                      cursor:"pointer",aspectRatio:"1",background:"var(--warm)",transition:"border-color 0.15s"}}
                  >
                    <img src={url} alt={key} style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                  </div>
                ))}
              </div>
            )}


            <label className="pm-label">First name</label>
            <input className="pm-input" value={firstName} onChange={e=>setFirstName(e.target.value)} placeholder="First name"/>

            <label className="pm-label">Dietary requirements</label>
            <div className="pm-chips">
              {DIETARY_OPTIONS.map(d=>(
                <button key={d} className={`pm-chip ${dietary.includes(d)?"on":""}`} onClick={()=>toggleItem(dietary,setDietary,d)}>{d}</button>
              ))}
            </div>

            <label className="pm-label">Allergens</label>
            <div className="pm-chips">
              {ALLERGEN_OPTIONS.map(a=>(
                <button key={a} className={`pm-chip ${allergens.includes(a)?"on":""}`} onClick={()=>toggleItem(allergens,setAllergens,a)}>{a}</button>
              ))}
            </div>

            <div className="pm-footer">
              <button className="btn btn-g" style={{flex:1}} onClick={()=>{setEditing(false);setError("");}}>Cancel</button>
              <button className="btn btn-p" style={{flex:1}} onClick={handleSave} disabled={loading}>{loading?"Saving...":"Save changes"}</button>
            </div>
          </>
        )}
      </div>
    </div>
    </>
  );
}


// ─── Ingredient Autocomplete Input ────────────────────────────────────────────
function IngredientInput({value, onChange, onSelectDBRow, allIngredients, ingredientDB, placeholder, className}){
  const [open, setOpen] = React.useState(false);
  const [q, setQ] = React.useState(value||"");
  const ref = React.useRef(null);

  React.useEffect(()=>{ setQ(value||""); }, [value]);

  React.useEffect(()=>{
    function handler(e){ if(ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener("mousedown", handler);
    return ()=>document.removeEventListener("mousedown", handler);
  },[]);

  const suggestions = q.length>=1
    ? (allIngredients||[]).filter(n=>n.toLowerCase().includes(q.toLowerCase())).slice(0,8)
    : [];

  const getDBRow=(name)=>(ingredientDB||[]).find(i=>i.name.toLowerCase()===name.toLowerCase());

  return(
    <div ref={ref} style={{position:"relative",flex:1}}>
      <input
        className={className||"rm-ing-input"}
        value={q}
        placeholder={placeholder}
        autoComplete="off"
        onChange={e=>{setQ(e.target.value);onChange(e.target.value);setOpen(true);}}
        onFocus={()=>setOpen(true)}
      />
      {open&&suggestions.length>0&&(
        <div style={{position:"absolute",top:"calc(100% + 2px)",left:0,right:0,zIndex:500,background:"white",border:"1.5px solid var(--sage)",borderRadius:8,boxShadow:"0 4px 16px rgba(0,0,0,0.15)",overflow:"hidden",maxHeight:260,overflowY:"auto"}}>
          {suggestions.map((s,i)=>{
            const dbRow=getDBRow(s);
            return(
              <div key={i}
                onMouseDown={e=>{
                  e.preventDefault();
                  setQ(s);
                  onChange(s);
                  // If DB row found, pass enriched data back to parent
                  if(dbRow&&onSelectDBRow)onSelectDBRow(dbRow);
                  setOpen(false);
                }}
                style={{padding:"8px 10px",fontSize:13,cursor:"pointer",color:"var(--bark)",borderBottom:i<suggestions.length-1?"1px solid var(--border)":"none",display:"flex",alignItems:"center",justifyContent:"space-between",gap:8}}
              >
                <span>{s}</span>
                {dbRow&&(
                  <span style={{fontSize:11,color:"var(--soft)",display:"flex",gap:6,flexShrink:0}}>
                    <span style={{background:"var(--cream)",padding:"1px 6px",borderRadius:10}}>{dbRow.category}</span>
                    <span style={{background:"var(--cream)",padding:"1px 6px",borderRadius:10}}>{dbRow.shelf_days}d</span>
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ReviewModal({recipe, onConfirm, onDiscard, normalise, allIngredients, ingredientDB, isAdmin, shareToAll, setShareToAll}){
  const [r, setR] = React.useState({...recipe});
  const [imgPreview, setImgPreview] = React.useState(recipe.image||"");
  const fileRef = React.useRef(null);

  const updIng=(i,k,v)=>setR(p=>{
    const ings=[...p.ingredients];
    ings[i]={...ings[i],[k]:["qty","shelfDays"].includes(k)?Number(v)||0:v};
    return{...p,ingredients:ings};
  });
  const remIng=i=>setR(p=>({...p,ingredients:p.ingredients.filter((_,j)=>j!==i)}));
  const addIng=()=>setR(p=>({...p,ingredients:[...p.ingredients,{name:"",qty:1,unit:"",shelfDays:7,category:"produce"}]}));
  const reNorm=()=>setR(p=>({...p,ingredients:p.ingredients.map(normalise)}));

  const css2=`
    .rm-overlay{position:fixed;inset:0;background:rgba(0,0,0,0.6);z-index:250;display:flex;align-items:flex-start;justify-content:center;padding:16px;overflow-y:auto;backdrop-filter:blur(4px);}
    .rm-modal{background:var(--cream);border-radius:var(--r);padding:24px;max-width:720px;width:100%;margin:auto;box-shadow:0 20px 60px rgba(0,0,0,0.35);}
    .rm-modal h3{font-size:22px;color:var(--bark);margin-bottom:4px;}
    .rm-sub{font-size:13px;color:var(--soft);margin-bottom:20px;}
    .rm-section{margin-bottom:20px;}
    .rm-section h4{font-size:14px;font-weight:600;color:var(--soft);text-transform:uppercase;letter-spacing:0.5px;margin-bottom:8px;display:flex;align-items:center;justify-content:space-between;}
    .rm-input{width:100%;padding:8px 12px;border-radius:8px;border:1.5px solid var(--border);font-family:'DM Sans',sans-serif;font-size:14px;outline:none;background:white;}
    .rm-input:focus{border-color:var(--terra);}
    .rm-textarea{width:100%;padding:8px 12px;border-radius:8px;border:1.5px solid var(--border);font-family:'DM Sans',sans-serif;font-size:14px;outline:none;background:white;resize:vertical;min-height:70px;}
    .rm-textarea:focus{border-color:var(--terra);}
    .rm-ing-row{display:grid;grid-template-columns:1fr 65px 60px 95px 26px;gap:4px;margin-bottom:5px;align-items:center;}
    .rm-ing-input{padding:6px 8px;border-radius:6px;border:1.5px solid var(--border);font-family:'DM Sans',sans-serif;font-size:12px;outline:none;width:100%;}
    .rm-ing-input:focus{border-color:var(--sage);}
    .rm-del{background:#fee2e2;border:none;border-radius:6px;color:#b91c1c;cursor:pointer;width:26px;height:26px;display:flex;align-items:center;justify-content:center;font-size:15px;}
    .rm-footer{display:flex;gap:10px;justify-content:flex-end;padding-top:16px;border-top:1.5px solid var(--border);margin-top:16px;flex-wrap:wrap;}
    .rm-img{width:100%;height:180px;object-fit:cover;border-radius:var(--rs);margin-bottom:8px;cursor:pointer;}
    .rm-img-placeholder{height:100px;border:2px dashed var(--border);border-radius:var(--rs);display:flex;align-items:center;justify-content:center;cursor:pointer;background:var(--warm);color:var(--soft);font-size:13px;margin-bottom:8px;}
    @media(max-width:600px){.rm-ing-row{grid-template-columns:1fr 1fr;}}
  `;

  return(
    <>
    <style>{css2}</style>
    <div className="rm-overlay">
      <div className="rm-modal">
        <h3>Review Before Saving</h3>
        <p className="rm-sub">Check and edit the extracted recipe, then confirm to add it to your library.</p>

        {/* Image */}
        <div className="rm-section">
          <h4>Photo</h4>
          {imgPreview
            ? <img className="rm-img" src={imgPreview} alt="recipe" onClick={()=>fileRef.current?.click()} onError={()=>setImgPreview("")}/>
            : <div className="rm-img-placeholder" onClick={()=>fileRef.current?.click()}>📷 Click to add a photo</div>
          }
          <input type="file" accept="image/*" ref={fileRef} style={{display:"none"}} onChange={e=>{
            const f=e.target.files[0]; if(!f)return;
            const url=URL.createObjectURL(f);
            setImgPreview(url);
            setR(p=>({...p,image:url}));
          }}/>
        </div>

        {/* Title + basics */}
        <div className="rm-section">
          <h4>Recipe Details</h4>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
            <div>
              <label style={{fontSize:11,color:"var(--soft)",display:"block",marginBottom:3}}>TITLE</label>
              <input className="rm-input" value={r.title} onChange={e=>setR(p=>({...p,title:e.target.value}))}/>
            </div>
            <div>
              <label style={{fontSize:11,color:"var(--soft)",display:"block",marginBottom:3}}>CUISINE</label>
              <input className="rm-input" value={r.cuisine} onChange={e=>setR(p=>({...p,cuisine:e.target.value}))}/>
            </div>
            <div>
              <label style={{fontSize:11,color:"var(--soft)",display:"block",marginBottom:3}}>COOK TIME (MINS)</label>
              <input className="rm-input" type="number" value={r.time} onChange={e=>setR(p=>({...p,time:Number(e.target.value)}))}/>
            </div>
            <div>
              <label style={{fontSize:11,color:"var(--soft)",display:"block",marginBottom:3}}>SERVINGS</label>
              <input className="rm-input" type="number" value={r.servings} onChange={e=>setR(p=>({...p,servings:Number(e.target.value)}))}/>
            </div>
          </div>
        </div>

        {/* Ingredients */}
        <div className="rm-section">
          <h4>
            Ingredients
            <div style={{display:"flex",gap:6}}>
              <button className="btn btn-s btn-sm" onClick={reNorm} title="Re-run auto-normalisation">↻ Normalise</button>
              <button className="btn btn-s btn-sm" onClick={addIng}>+ Add</button>
            </div>
          </h4>
          <div style={{display:"grid",gridTemplateColumns:"1fr 55px 55px 80px 1fr 26px",gap:"3px 4px",fontSize:10,color:"var(--soft)",marginBottom:4,padding:"0 2px"}}>
            <span>Name</span><span>Qty</span><span>Unit</span><span>Category</span><span>Prep notes</span><span/>
          </div>
          {r.ingredients.map((ing,i)=>(
            <div key={i} className="rm-ing-row">
              <IngredientInput value={ing.name}
                onChange={v=>updIng(i,"name",v)}
                onSelectDBRow={row=>{
                  updIng(i,"category",row.category);
                  updIng(i,"shelfDays",row.shelf_days);
                  if(row.default_unit)updIng(i,"unit",row.default_unit);
                }}
                allIngredients={allIngredients}
                ingredientDB={ingredientDB||[]}
                placeholder="Ingredient name"/>
              <input className="rm-ing-input" type="number" value={ing.qty} onChange={e=>updIng(i,"qty",e.target.value)}/>
              <input className="rm-ing-input" value={ing.unit} onChange={e=>updIng(i,"unit",e.target.value)}/>
              <select className="rm-ing-input" value={ing.category} onChange={e=>updIng(i,"category",e.target.value)}>
                {["produce","meat","fish","dairy","pantry"].map(c=><option key={c} value={c}>{c}</option>)}
              </select>
              <input className="rm-ing-input" value={ing.notes||""} onChange={e=>updIng(i,"notes",e.target.value)} placeholder="e.g. diced"/>
              <button className="rm-del" onClick={()=>remIng(i)}>×</button>
            </div>
          ))}
        </div>

        {/* Method */}
        <div className="rm-section">
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
            <h4>Method</h4>
            <button className="btn btn-s btn-sm" onClick={()=>setR(p=>({...p,method:[...p.method,""]}))}>+ Step</button>
          </div>
          {r.method.map((step,si)=>(
            <div key={si} style={{display:"flex",gap:6,marginBottom:6,alignItems:"flex-start"}}>
              <div style={{minWidth:20,height:20,borderRadius:"50%",background:"var(--terra)",color:"white",fontSize:10,fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,marginTop:4}}>{si+1}</div>
              <textarea className="rm-textarea" rows={2}
                style={{flex:1,marginBottom:0,minHeight:44,resize:"vertical"}}
                value={step}
                placeholder={`Step ${si+1}…`}
                onChange={e=>setR(p=>{const m=[...p.method];m[si]=e.target.value;return{...p,method:m};})}
              />
              <button className="rm-del" style={{marginTop:4}} onClick={()=>setR(p=>({...p,method:p.method.filter((_,j)=>j!==si)}))}>×</button>
            </div>
          ))}
          {!r.method.length&&(
            <p style={{fontSize:12,color:"var(--soft)"}}>No steps yet — click + Step</p>
          )}
        </div>

        {isAdmin&&(
          <div style={{padding:"12px 0",borderTop:"1px solid var(--border)",marginBottom:8}}>
            <label style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer",fontSize:14,color:"var(--bark)"}}>
              <div onClick={()=>setShareToAll(p=>!p)} style={{width:44,height:24,borderRadius:12,background:shareToAll?"#6366f1":"var(--border)",transition:"background 0.2s",position:"relative",flexShrink:0,cursor:"pointer"}}>
                <div style={{position:"absolute",top:2,left:shareToAll?22:2,width:20,height:20,borderRadius:"50%",background:"white",transition:"left 0.2s",boxShadow:"0 1px 4px rgba(0,0,0,0.2)"}}/>
              </div>
              <div>
                <div style={{fontWeight:600}}>⭐ Add to shared library</div>
                <div style={{fontSize:12,color:"var(--soft)"}}>Visible to all Platewise users</div>
              </div>
            </label>
          </div>
        )}
        <div className="rm-footer">
          <button className="rm-del" onClick={onDiscard}>✕ Discard</button>
          <button className="btn btn-p" onClick={()=>onConfirm({...r,image:imgPreview||r.image},false)}>
            "✓ Confirm & Save"
          </button>
        </div>
      </div>
    </div>
    </>
  );
}


// ─── Photo Scan Screen ────────────────────────────────────────────────────────
function PhotoScanScreen({onBack, onScanned, toast}){
  const [photos,setPhotos]=React.useState([]); // up to 2 images: [{preview, data}]
  const [scanning,setScanning]=React.useState(false);
  const [scanSteps,setScanSteps]=React.useState([]);
  const [dragOver,setDragOver]=React.useState(false);
  const [activeSlot,setActiveSlot]=React.useState(0); // which slot we're adding to
  const uploadRef=React.useRef(null);
  const cameraRef=React.useRef(null);

  const compressImage=(file)=>new Promise((resolve)=>{
    const img=new Image();
    const objectUrl=URL.createObjectURL(file);
    img.onload=()=>{
      URL.revokeObjectURL(objectUrl);
      const MAX=1200;
      const scale=Math.min(1,MAX/Math.max(img.width,img.height));
      const canvas=document.createElement("canvas");
      canvas.width=Math.round(img.width*scale);
      canvas.height=Math.round(img.height*scale);
      canvas.getContext("2d").drawImage(img,0,0,canvas.width,canvas.height);
      const compressed=canvas.toDataURL("image/jpeg",0.8);
      resolve({preview:compressed,data:compressed.split(",")[1]});
    };
    img.src=objectUrl;
  });

  const handlePhotoSelect=async(file,slot)=>{
    if(!file||!file.type.startsWith("image/"))return;
    setScanSteps([]);
    const compressed=await compressImage(file);
    setPhotos(p=>{
      const next=[...p];
      next[slot]=compressed;
      return next;
    });
  };

  const handleInputChange=(e,slot)=>{
    const f=e.target.files[0];
    if(f)handlePhotoSelect(f,slot);
    e.target.value="";
  };

  const openUpload=(slot)=>{ setActiveSlot(slot); uploadRef.current?.click(); };
  const openCamera=(slot)=>{ setActiveSlot(slot); cameraRef.current?.click(); };
  const removePhoto=(slot)=>setPhotos(p=>{ const next=[...p]; next.splice(slot,1); return next; });

  const handleScan=async()=>{
    if(!photos.length||scanning)return;
    setScanning(true);
    setScanSteps([
      {label:"Reading image",status:"active"},
      {label:"Identifying recipe",status:"pending"},
      {label:"Extracting ingredients",status:"pending"},
      {label:"Structuring method",status:"pending"},
      {label:"Finalising recipe",status:"pending"},
    ]);
    try{
      setTimeout(()=>setScanSteps(p=>p.map((s,i)=>i===0?{...s,status:"done"}:i===1?{...s,status:"active"}:s)),500);
      // Build content array — include all images
      const imageContent=photos.map(p=>({type:"image",source:{type:"base64",media_type:"image/jpeg",data:p.data}}));
      const textPrompt=photos.length>1
        ?"These are two images of the same recipe (front and back of a page). Combine them to extract the complete recipe."
        :"Extract the recipe from this image.";
      const res=await fetch("https://platewise-ai.elliotmignot.workers.dev",{
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body:JSON.stringify({
          model:"claude-haiku-4-5-20251001",
          max_tokens:2000,
          messages:[{
            role:"user",
            content:[
              ...imageContent,
              {type:"text",text:`You are a recipe extraction assistant. ${textPrompt}

Return ONLY valid JSON — no markdown, no explanation:
{
  "title": "Recipe name",
  "time": 30,
  "servings": 4,
  "cuisine": "Italian",
  "tags": ["tag1","tag2"],
  "ingredients": [
    {"name":"Ingredient","qty":100,"unit":"g","shelfDays":7,"category":"produce"}
  ],
  "method": ["Step 1","Step 2"]
}

Rules:
- time = total minutes as a number
- servings = number
- category = one of: produce, meat, fish, dairy, pantry
- unit = g, kg, ml, l, tsp, tbsp, cup, or "" for whole items
- shelfDays = estimated freshness in days
- tags = 2-4 descriptive tags
- If no recipe visible, return {"error":"No recipe found"}`}
            ]
          }]
        })
      });
      setScanSteps(p=>p.map((s,i)=>i<=1?{...s,status:"done"}:i===2?{...s,status:"active"}:s));
      const data=await res.json();
      const raw=data.content?.map(b=>b.text||"").join("")||"";
      setScanSteps(p=>p.map((s,i)=>i<=2?{...s,status:"done"}:i===3?{...s,status:"active"}:s));
      const clean=raw.replace(/```json|```/g,"").trim();
      const parsed=JSON.parse(clean);
      if(parsed.error){
        setScanSteps(p=>p.map(s=>({...s,status:"error"})));
        toast(parsed.error,"error");
        setScanning(false);
        return;
      }
      setScanSteps(p=>p.map((s,i)=>i<=3?{...s,status:"done"}:i===4?{...s,status:"active"}:s));
      // Use first photo as default recipe image
      const recipe={
        id:Date.now(),
        image:photos[0]?.preview||"",
        is_public:false,
        ...parsed,
        tags:Array.isArray(parsed.tags)?parsed.tags:[],
        ingredients:Array.isArray(parsed.ingredients)?parsed.ingredients:[],
        method:Array.isArray(parsed.method)?parsed.method:[],
      };
      setScanSteps(p=>p.map(s=>({...s,status:"done"})));
      // Auto-generate AI food photo if no image was captured
      if(!recipe.image){
        try{
          const prompt=encodeURIComponent(`Professional food photography of ${recipe.title}, overhead shot, natural lighting, cookbook style, beautifully plated, delicious`);
          recipe.image=`https://image.pollinations.ai/prompt/${prompt}?width=512&height=512&nologo=true&model=turbo`;
        }catch(e){ /* image generation optional — continue without */ }
      }
      setTimeout(()=>onScanned(recipe),600);
    }catch(e){
      setScanSteps(p=>p.map(s=>s.status==="active"?{...s,status:"error"}:s));
      toast("Scan failed: "+e.message,"error");
      setScanning(false);
    }
  };

  const canAddMore=photos.length<2&&!scanning;

  return(
    <div>
      <div className="sh" style={{display:"flex",alignItems:"center",gap:12}}>
        <button onClick={onBack} style={{background:"none",border:"none",cursor:"pointer",color:"var(--soft)",fontSize:20,lineHeight:1,padding:0}}>←</button>
        <div><h2>Scan a Recipe</h2><p>Photograph a recipe book, magazine or handwritten card</p></div>
      </div>

      {/* Image slots */}
      {photos.length===0?(
        <>
          {/* Empty state — show upload/camera choice */}
          <div
            onDragOver={e=>{e.preventDefault();setDragOver(true);}}
            onDragLeave={()=>setDragOver(false)}
            onDrop={e=>{e.preventDefault();setDragOver(false);handlePhotoSelect(e.dataTransfer.files[0],0);}}
            style={{
              border:`2px dashed ${dragOver?"var(--terra)":"var(--border)"}`,
              borderRadius:"var(--r)",background:dragOver?"#fff5f3":"white",
              minHeight:180,display:"flex",flexDirection:"column",alignItems:"center",
              justifyContent:"center",marginBottom:12,padding:24,gap:20,
            }}
          >
            <div style={{textAlign:"center"}}>
              <div style={{fontSize:40,marginBottom:8}}>📄</div>
              <div style={{fontSize:15,fontWeight:600,color:"var(--bark)",marginBottom:4}}>Add a recipe image</div>
              <div style={{fontSize:13,color:"var(--soft)"}}>Up to 2 images — useful for front and back of a page</div>
            </div>
            <div style={{display:"flex",gap:10,flexWrap:"wrap",justifyContent:"center"}}>
              <button className="btn btn-p" style={{display:"flex",alignItems:"center",gap:8}} onClick={()=>openCamera(0)}>
                📷 Take Photo
              </button>
              <button className="btn btn-s" style={{display:"flex",alignItems:"center",gap:8}} onClick={()=>openUpload(0)}>
                🖼 Upload Image
              </button>
            </div>
          </div>
        </>
      ):(
        <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:12}}>
          {photos.map((photo,idx)=>(
            <div key={idx} style={{position:"relative",borderRadius:"var(--r)",overflow:"hidden",border:"1.5px solid var(--border)",background:"white"}}>
              <img src={photo.preview} alt={`Scan ${idx+1}`} style={{width:"100%",maxHeight:280,objectFit:"contain",display:"block"}}/>
              <div style={{position:"absolute",top:8,left:8,background:"rgba(0,0,0,0.6)",color:"white",fontSize:11,fontWeight:600,padding:"3px 10px",borderRadius:20}}>
                {idx===0?"📄 Page 1":"📄 Page 2"}
              </div>
              {!scanning&&(
                <button onClick={()=>removePhoto(idx)} style={{position:"absolute",top:8,right:8,background:"#dc2626",color:"white",border:"none",borderRadius:"50%",width:28,height:28,cursor:"pointer",fontSize:14,display:"flex",alignItems:"center",justifyContent:"center"}}>✕</button>
              )}
            </div>
          ))}
          {canAddMore&&(
            <div style={{display:"flex",gap:8}}>
              <button className="btn btn-s" style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",gap:6,padding:"10px"}} onClick={()=>openCamera(photos.length)}>
                📷 Add Page 2 (Camera)
              </button>
              <button className="btn btn-s" style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",gap:6,padding:"10px"}} onClick={()=>openUpload(photos.length)}>
                🖼 Add Page 2 (Upload)
              </button>
            </div>
          )}
        </div>
      )}

      {/* Hidden inputs */}
      <input ref={uploadRef} type="file" accept="image/*" style={{display:"none"}} onChange={e=>handleInputChange(e,activeSlot)}/>
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" style={{display:"none"}} onChange={e=>handleInputChange(e,activeSlot)}/>

      {photos.length===0&&(
        <div style={{background:"#fdf4ff",border:"1px solid #e9d5ff",borderRadius:"var(--r)",padding:"12px 16px",marginBottom:16,display:"flex",gap:10,alignItems:"flex-start"}}>
          <span style={{fontSize:18,flexShrink:0}}>💡</span>
          <div style={{fontSize:13,color:"#6b21a8",lineHeight:1.6}}>
            <strong>Tips:</strong> Make sure text is sharp and well-lit. If a recipe spans two pages, add both. The first photo will become your recipe's cover image.
          </div>
        </div>
      )}

      {scanSteps.length>0&&(
        <div className="psteps" style={{marginBottom:16}}>
          {scanSteps.map((s,i)=>(
            <span key={i} className={`pstep ${s.status}`}>
              {s.status==="done"?"✓":s.status==="active"?<span className="spin">⟳</span>:s.status==="error"?"✗":"○"} {s.label}
            </span>
          ))}
        </div>
      )}

      <button
        className="btn btn-p"
        style={{width:"100%",padding:"14px",fontSize:15,opacity:!photos.length||scanning?0.45:1,transition:"opacity 0.2s"}}
        onClick={handleScan}
        disabled={!photos.length||scanning}
      >
        {scanning?"🔍 Scanning…":`📷 Extract Recipe${photos.length>1?" (2 pages)":""}`}
      </button>
    </div>
  );
}


// ─── Delete Account Section (used in Settings tab) ───────────────────────────
function DeleteAccountSection({onDelete}){
  const [confirmDel,setConfirmDel]=React.useState(false);
  return(
    <div style={{marginBottom:28,paddingTop:20,borderTop:"1px solid var(--border)"}}>
      <h3 style={{fontSize:18,marginBottom:6,color:"#b91c1c"}}>Danger Zone</h3>
      <p style={{fontSize:13,color:"var(--soft)",marginBottom:12}}>Permanently delete your account and all associated data. This cannot be undone.</p>
      {!confirmDel?(
        <button className="btn btn-del" onClick={()=>setConfirmDel(true)}>🗑 Delete My Account</button>
      ):(
        <div style={{background:"#fef2f2",border:"1.5px solid #fca5a5",borderRadius:"var(--r)",padding:16}}>
          <p style={{fontSize:13,color:"#b91c1c",fontWeight:600,marginBottom:8}}>⚠️ Are you absolutely sure?</p>
          <p style={{fontSize:13,color:"var(--soft)",marginBottom:14}}>This will permanently delete your account, all your recipes, meal plans and data. There is no going back.</p>
          <div style={{display:"flex",gap:8}}>
            <button className="btn btn-g" onClick={()=>setConfirmDel(false)}>Cancel</button>
            <button className="btn btn-del" onClick={()=>{onDelete();setConfirmDel(false);}}>Yes, delete everything</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Edit Recipe Panel ────────────────────────────────────────────────────────
function EditPanel({recipe,onSave,onCancel,onPhoto,uploading,toast,allIngredients,ingredientDB,isAdmin,isManualAdd,canTogglePublic=true}){
  const [f,setF]=useState({
    ...recipe,
    tagsStr:recipe.tags?.join(", ")||"",
    methodStr:recipe.method?.join("\n")||"",
    ingredients:recipe.ingredients?.length?[...recipe.ingredients]:[],
  });
  const [preview,setPreview]=useState(recipe.image||"");
  const [generatingImg,setGeneratingImg]=useState(false);

  const [isPublic,setIsPublic]=useState(recipe.is_public||false);
  // Pre-confirm for recipes already published — user is just editing, not re-confirming
  const [publicConfirmed,setPublicConfirmed]=useState(recipe.is_public||false);
  const fileRef=useRef(null);
  const isDragRef=useRef(false);

  const upd=(k,v)=>setF(p=>({...p,[k]:v}));
  const setIng=(i,k,v)=>setF(p=>{const ings=[...p.ingredients];ings[i]={...ings[i],[k]:["qty","shelfDays"].includes(k)?Number(v)||0:v};return{...p,ingredients:ings};});
  const addIng=()=>setF(p=>({...p,ingredients:[...p.ingredients,{name:"",qty:100,unit:"g",shelfDays:7,category:"produce"}]}));
  const remIng=i=>setF(p=>({...p,ingredients:p.ingredients.filter((_,j)=>j!==i)}));

  const handleFile=file=>{
    if(!file?.type.startsWith("image/")){toast("Select an image file","error");return;}
    if(file.size>5*1024*1024){toast("Max 5MB","error");return;}
    const url=URL.createObjectURL(file);
    setPreview(url);
    onPhoto(file);
  };

  const save=(makeShared=false)=>{
    if(!f.title.trim()){toast("Title is required","error");return;}
    onSave({
      ...f,id:recipe.id,
      time:Number(f.time)||30,servings:Number(f.servings)||4,
      tags:f.tagsStr.split(",").map(t=>t.trim()).filter(Boolean),
      method:f.methodStr.split("\n").map(s=>s.trim()).filter(Boolean),
      image:preview||recipe.image||"",
      ingredients:f.ingredients,
      is_shared:makeShared,
      is_public:(isManualAdd&&canTogglePublic)?isPublic:!!recipe.is_public,
    });
  };

  return(
    <div style={{maxWidth:800,margin:"0 auto"}}>
      <div style={{display:"flex",gap:8,marginBottom:20,flexWrap:"wrap"}}>
        <button className="btn btn-g" onClick={onCancel}>← Cancel</button>
        <h2 style={{fontSize:22,color:"var(--bark)",flex:1,display:"flex",alignItems:"center"}}>{recipe.title||"New Recipe"}</h2>
        <button className="btn btn-p" style={{flex:"none"}} onClick={save}>💾 Save</button>
      </div>

      {/* Photo */}
      <div className="fgrp">
        <label className="flabel">Recipe Photo</label>
        <div className="pzone" onClick={()=>fileRef.current?.click()}
          onDragOver={e=>{e.preventDefault();isDragRef.current=true;}}
          onDrop={e=>{e.preventDefault();handleFile(e.dataTransfer.files[0]);}}>
          {preview?<img src={preview} alt="preview" onError={()=>setPreview("")}/>:<div className="picon">📷</div>}
          <p>{uploading?"Uploading…":"Click or drag & drop a photo (max 5MB)"}</p>
        </div>
        <input type="file" accept="image/*" ref={fileRef} style={{display:"none"}} onChange={e=>handleFile(e.target.files[0])}/>
        {/* AI image generation */}
        <button
          className="btn btn-s btn-sm"
          style={{marginTop:8,width:"100%",display:"flex",alignItems:"center",justifyContent:"center",gap:6,opacity:generatingImg?0.6:1}}
          disabled={generatingImg}
          onClick={async()=>{
            if(!f.title.trim()){toast("Add a title first","error");return;}
            setGeneratingImg(true);
            try{
              const prompt=encodeURIComponent(`Professional food photography of ${f.title}, overhead shot, natural lighting, cookbook style, beautifully plated, high resolution`);
              const url=`https://image.pollinations.ai/prompt/${prompt}?width=512&height=512&nologo=true&model=turbo`;
              // Pre-load to confirm it worked before setting
              const img=new Image();
              img.onload=()=>{setPreview(url);setGeneratingImg(false);toast("✨ AI image generated!","success");};
              img.onerror=()=>{setGeneratingImg(false);toast("Image generation failed — try again","error");};
              img.src=url;
            }catch(e){
              setGeneratingImg(false);
              toast("Image generation failed","error");
            }
          }}
        >
          {generatingImg?<><span className="spin" style={{display:"inline-block"}}>⟳</span> Generating…</>:"✨ Generate AI Food Photo"}
        </button>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
        <div className="fgrp"><label className="flabel">Title *</label><input className="finput" value={f.title} onChange={e=>upd("title",e.target.value)} placeholder="Recipe name"/></div>
        <div className="fgrp"><label className="flabel">Cuisine</label><input className="finput" value={f.cuisine} onChange={e=>upd("cuisine",e.target.value)} placeholder="e.g. Italian"/></div>
        <div className="fgrp"><label className="flabel">Cook Time (mins)</label><input className="finput" type="number" value={f.time} onChange={e=>upd("time",e.target.value)}/></div>
        <div className="fgrp"><label className="flabel">Base Servings</label><input className="finput" type="number" value={f.servings} onChange={e=>upd("servings",e.target.value)}/></div>
      </div>

      <div className="fgrp"><label className="flabel">Tags (comma separated)</label><input className="finput" value={f.tagsStr} onChange={e=>upd("tagsStr",e.target.value)} placeholder="quick, healthy, vegetarian"/></div>

      <div className="fgrp">
        <label className="flabel" style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          Ingredients
          <button className="btn btn-s btn-sm" onClick={addIng}>+ Add Row</button>
        </label>
        <div style={{display:"grid",gridTemplateColumns:"1fr 50px 50px 72px 45px 1fr 28px",gap:"3px 6px",fontSize:11,color:"var(--soft)",padding:"0 2px",marginBottom:6}}>
          <span>Name</span><span>Qty</span><span>Unit</span><span>Category</span><span title="Shelf life in days">Days</span><span>Prep notes (optional)</span><span/>
        </div>
        {f.ingredients.map((ing,i)=>(
          <div key={i} className="ingrow-edit">
            <IngredientInput value={ing.name}
              onChange={v=>{
                setIng(i,"name",v);
                if(!ing.shelfDays||ing.shelfDays===7||ing.shelfDays===14){
                  setIng(i,"shelfDays",guessShelfLife(v,ing.category));
                }
              }}
              onSelectDBRow={row=>{
                setIng(i,"category",row.category||ing.category);
                setIng(i,"shelfDays",row.shelf_days||ing.shelfDays);
                if(row.default_unit&&!ing.unit)setIng(i,"unit",row.default_unit);
              }}
              allIngredients={allIngredients}
              ingredientDB={ingredientDB||[]}
              placeholder="e.g. Garlic" className="ingi"/>
            <input className="ingi" type="number" value={ing.qty} onChange={e=>setIng(i,"qty",e.target.value)}/>
            <input className="ingi" value={ing.unit} onChange={e=>setIng(i,"unit",e.target.value)} placeholder="g / ml…"/>
            <select className="ingi" value={ing.category} onChange={e=>{setIng(i,"category",e.target.value);setIng(i,"shelfDays",guessShelfLife(ing.name,e.target.value));}}>
              {["produce","meat","fish","dairy","pantry"].map(c=><option key={c} value={c}>{c}</option>)}
            </select>
            <input className="ingi" type="number" value={ing.shelfDays||""} onChange={e=>setIng(i,"shelfDays",Number(e.target.value)||0)} placeholder="days" title="Shelf life in days"/>
            <input className="ingi" value={ing.notes||""} onChange={e=>setIng(i,"notes",e.target.value)} placeholder="e.g. finely diced"/>
            <button className="ingdel" onClick={()=>remIng(i)}>×</button>
          </div>
        ))}
        {!f.ingredients.length&&<p style={{fontSize:13,color:"var(--soft)",padding:"8px 0"}}>No ingredients yet — click + Add Row</p>}
      </div>

      <div className="fgrp">
        <label className="flabel" style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <span>Method</span>
          <button className="btn btn-s btn-sm" onClick={()=>upd("methodStr",f.methodStr+"\n")}>+ Add Step</button>
        </label>
        {(f.methodStr||"").split("\n").map((step,si)=>(
          <div key={si} style={{display:"flex",gap:6,marginBottom:6,alignItems:"flex-start"}}>
            <div style={{minWidth:22,height:22,borderRadius:"50%",background:"var(--terra)",color:"white",fontSize:11,fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,marginTop:6}}>{si+1}</div>
            <textarea
              className="ftarea"
              rows={2}
              style={{flex:1,marginBottom:0,resize:"vertical"}}
              value={step}
              placeholder={`Step ${si+1}…`}
              onChange={e=>{
                const steps=f.methodStr.split("\n");
                steps[si]=e.target.value;
                upd("methodStr",steps.join("\n"));
              }}
            />
            <button className="ingdel" style={{marginTop:6,flexShrink:0}} onClick={()=>{
              const steps=f.methodStr.split("\n").filter((_,i)=>i!==si);
              upd("methodStr",steps.join("\n"));
            }}>×</button>
          </div>
        ))}
        {(!f.methodStr||!f.methodStr.trim())&&(
          <p style={{fontSize:13,color:"var(--soft)",padding:"4px 0"}}>No steps yet — click + Add Step</p>
        )}
      </div>

      {/* Public/Private toggle — manual adds only, and never for guests/non-authors */}
      {isManualAdd&&canTogglePublic&&(
        <div style={{padding:"14px 0",borderTop:"1px solid var(--border)",marginBottom:4}}>
          <div style={{fontSize:12,fontWeight:600,color:"var(--soft)",textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:10}}>
            Visibility {recipe.is_public&&<span style={{fontSize:11,fontWeight:400,textTransform:"none",color:"#6366f1"}}>· currently public</span>}
          </div>
          <div style={{display:"flex",gap:8,marginBottom:12}}>
            <button
              onClick={()=>{setIsPublic(false);setPublicConfirmed(false);}}
              style={{flex:1,padding:"10px",borderRadius:10,border:`2px solid ${!isPublic?"var(--terra)":"var(--border)"}`,background:!isPublic?"#fff5f3":"white",color:!isPublic?"var(--terra)":"var(--soft)",fontFamily:"'DM Sans',sans-serif",fontSize:13,fontWeight:600,cursor:"pointer"}}
            >🔒 Private</button>
            <button
              onClick={()=>setIsPublic(true)}
              style={{flex:1,padding:"10px",borderRadius:10,border:`2px solid ${isPublic?"#6366f1":"var(--border)"}`,background:isPublic?"#eef2ff":"white",color:isPublic?"#6366f1":"var(--soft)",fontFamily:"'DM Sans',sans-serif",fontSize:13,fontWeight:600,cursor:"pointer"}}
            >🌍 Public</button>
          </div>
          {!isPublic&&<p style={{fontSize:12,color:"var(--soft)"}}>Only visible in your personal library.</p>}
          {isPublic&&(
            <div style={{background:"#eef2ff",borderRadius:10,padding:"12px 14px"}}>
              <p style={{fontSize:13,color:"#4338ca",marginBottom:8}}>Sharing this recipe will make it visible to all Platewise users, who can save it to their own library.</p>
              <label style={{display:"flex",alignItems:"flex-start",gap:8,cursor:"pointer",fontSize:13,color:"#4338ca"}}>
                <input type="checkbox" checked={publicConfirmed} onChange={e=>setPublicConfirmed(e.target.checked)} style={{marginTop:2,flexShrink:0}}/>
                <span>I confirm this is my own original recipe and I have the right to share it publicly.</span>
              </label>
            </div>
          )}
        </div>
      )}

      {isAdmin&&(
        <div style={{padding:"12px 0",borderTop:"1px solid var(--border)",marginBottom:12}}>
          <label style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer",fontSize:14,color:"var(--bark)"}}>
            <div
              onClick={()=>setShareMode(p=>!p)}
              style={{width:44,height:24,borderRadius:12,background:shareMode?"#6366f1":"var(--border)",transition:"background 0.2s",position:"relative",flexShrink:0,cursor:"pointer"}}
            >
              <div style={{position:"absolute",top:2,left:shareMode?22:2,width:20,height:20,borderRadius:"50%",background:"white",transition:"left 0.2s",boxShadow:"0 1px 4px rgba(0,0,0,0.2)"}}/>
            </div>
            <div>
              <div style={{fontWeight:600}}>⭐ Add to shared library</div>
              <div style={{fontSize:12,color:"var(--soft)"}}>Visible to all users of Platewise</div>
            </div>
          </label>
        </div>
      )}
      <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:8,paddingBottom:40}}>
        <button className="btn btn-g" onClick={onCancel}>Cancel</button>
        <button
          className="btn btn-p"
          onClick={()=>{
            if(isManualAdd&&canTogglePublic&&isPublic&&!publicConfirmed){
              toast("Please confirm you own the content before sharing publicly","error");
              return;
            }
            save(false);
          }}
          style={{opacity:isManualAdd&&canTogglePublic&&isPublic&&!publicConfirmed?0.4:1,transition:"opacity 0.2s"}}
        >
          {isManualAdd&&canTogglePublic&&isPublic?"🌍 Save & Share with Community":"💾 Save Recipe"}
        </button>
      </div>
    </div>
  );
}

export default App;
